/**
 ItemDetails.js
 
 Purpose:
 - Provide an editable form for viewing and updating details of an item.
 */

import TypeChipLink from '../components/common/TypeChipLink';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import useStatusChipFit from '../components/itemDetails/useStatusChipFit';
import { DEMOTE_ORDER } from '../components/itemDetails/statusChipFit';
import useSwipeNavigate from '../components/itemDetails/useSwipeNavigate';
import PropTypes from 'prop-types';
import {
  Box, Typography, Tabs, Tab,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  CircularProgress, Alert, FormControl, InputLabel, Select, MenuItem, TextField, useMediaQuery
} from '@mui/material';
import RichTextField from '../components/editor/RichTextField';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { requirementsApi } from '../services/api';
import { iamApi } from '../services/iamApi';
import { usePref } from '../services/prefs';
import { resolveIdDisplay } from '../utils/itemId';
import { approvalToneOf, verificationToneOf } from '../utils/statusTone';
import ApprovalReasonDialog from '../components/itemDetails/review/ApprovalReasonDialog';
import VerificationRecordDialog from '../components/itemDetails/VerificationRecordDialog';
import { saveDraft, loadDraft, clearDraft } from '../utils/itemDraft';
import { pushRecentItem } from '../utils/recentItems';
import useAutosave from '../hooks/useAutosave';
import useReviewActivity from '../hooks/useReviewActivity';
import { dequeueSave } from '../utils/offlineQueue';
import FieldRenderer from '../components/itemDetails/FieldRenderer';

// Import components
import TabPanel from '../components/itemDetails/TabPanel';
import AttributesTab from '../components/itemDetails/AttributesTab';
import {
  attributesChanged, attributesToRows, rowsToAttributes, duplicateAttributeNames,
} from '../utils/attributeChanges';
import { toHistoryRows } from '../utils/formatHistory';
import { htmlToText } from '../utils/htmlText';
import DiffViewer from '../components/common/DiffViewer';
import RelationshipsTab from '../components/itemDetails/RelationshipsTab';
import ImpactTab from '../components/itemDetails/ImpactTab';
import HistoryTab from '../components/itemDetails/HistoryTab';
import DocumentTab from '../components/itemDetails/DocumentTab';
import BreakdownTab from '../components/itemDetails/BreakdownTab';
import CommentsTab from '../components/itemDetails/CommentsTab';
import ItemDetailsToolbar from '../components/itemDetails/ItemDetailsToolbar';
import ChangeRequestClosurePanel from '../components/itemDetails/ChangeRequestClosure';
import AiDialog from '../components/itemDetails/ai/AiDialog';
import EntitiesDialog from '../components/itemDetails/ai/EntitiesDialog';
import ReviewFeedbackDialog from '../components/itemDetails/ai/ReviewFeedbackDialog';
import DecomposeDialog from '../components/itemDetails/ai/DecomposeDialog';
import TestCaseDialog from '../components/itemDetails/ai/TestCaseDialog';
import ImplementationDialog from '../components/itemDetails/ai/ImplementationDialog';
import CalculationsDialog from '../components/dialogs/CalculationsDialog';
import AddDialog from '../components/AddDialog';
import DeleteDialog from '../components/dialogs/DeleteDialog';
import ConfirmDialog from '../components/dialogs/ConfirmDialog';
import ConflictMergeDialog from '../components/dialogs/ConflictMergeDialog';
import UnifiedContainerSelector from '../components/tree/UnifiedContainerSelector';

// Import hooks
import useAiFunctions from '../hooks/useAiFunctions';
import useCalculations from '../hooks/useCalculations';

// #943: attributesToRows(...) itself returns the plain {name,type,value,...}
// shape its own round-trip tests assert exactly (attributeChanges.test.js) — so
// `_key` (a React `key=` identity only) is stamped on here, at every call site
// that feeds AttributesTab's editable rows into state, never inside the shared
// util. rowsToAttributes(...)/serializeAttributeRows(...) both read a fixed
// field whitelist, so the extra property never reaches the save payload or the
// dirty-check. Without it, `key={index}` let a mid-list delete/reorder hand one
// row's <TextField> (and any in-progress edit) to the wrong row.
const withRowKeys = (rows) => (rows || []).map((r) => ({ ...r, _key: r && r._key ? r._key : uuidv4() }));

// Diff a historical snapshot's attributes (dict) against the current rows so the
// Compare modal surfaces attribute changes, not just title/description.
function computeAttributeDiff(snapshotAttrs, currentRows) {
  const toMap = (rows) => {
    const m = {};
    (rows || []).forEach((r) => {
      if (r && r.name) m[r.name] = r.value == null ? '' : String(r.value);
    });
    return m;
  };
  const oldMap = toMap(attributesToRows(snapshotAttrs || {}));
  const newMap = toMap(currentRows);
  const names = Array.from(new Set([...Object.keys(oldMap), ...Object.keys(newMap)])).sort();
  const changes = [];
  names.forEach((name) => {
    const before = oldMap[name];
    const after = newMap[name];
    if (before === after) return;
    if (before === undefined) changes.push({ name, kind: 'added', before: '', after });
    else if (after === undefined) changes.push({ name, kind: 'removed', before, after: '' });
    else changes.push({ name, kind: 'changed', before, after });
  });
  return changes;
}

// Diff a historical snapshot's relationships against the item's CURRENT
// relationships state, keyed by (target id/display_id, link_type, direction) —
// same key shape used by the backend's restore-reconcile, since list order/index
// isn't stable across snapshots and isn't what identifies "the same relationship".
function computeRelationshipDiff(snapshotRelationships, currentRelationships) {
  const key = (rel) => `${rel.id || rel.targetId || rel.display_id || rel.displayId}::${rel.link_type || rel.type}::${rel.direction}`;
  const label = (rel) => {
    const chip = rel.display_id || rel.displayId || rel.id || rel.targetId;
    const title = rel.title || (rel.item ? String(rel.item).split(': ').slice(1).join(': ') : '');
    return title ? `${chip}: ${title}` : String(chip);
  };
  const snapshotMap = new Map((snapshotRelationships || []).map((r) => [key(r), r]));
  const currentMap = new Map((currentRelationships || []).map((r) => [key(r), {
    id: r.targetId, display_id: r.displayId, title: (r.item || '').split(': ').slice(1).join(': '),
    link_type: r.type, direction: r.direction,
  }]));

  const added = [];
  const removed = [];
  currentMap.forEach((rel, k) => { if (!snapshotMap.has(k)) added.push(rel); });
  snapshotMap.forEach((rel, k) => { if (!currentMap.has(k)) removed.push(rel); });
  return { added, removed, label };
}

// Stable key for a relationship row (survives list re-grouping/re-indexing,
// unlike the array index RelationshipsTab previously keyed deletes on).
function relKey(rel) {
  return `${rel.targetId}::${rel.type}::${rel.direction}`;
}

// Map the API's `relationships` (id/display_id/link_type/direction) onto the
// row shape RelationshipsTab renders. Every place that re-fetches an item
// (initial load, after linking, after a save, after a restore) must run its
// response through this so the tab actually reflects what the server has —
// a restore that skipped this left the Relationships tab showing stale data
// even though the backend had genuinely restored the link.
function mapRelationships(data) {
  if (!data?.relationships || !Array.isArray(data.relationships)) return [];
  return data.relationships.map((rel) => ({
    type: rel.link_type || 'related',
    item: `${rel.id}: ${rel.title || ''}`,
    displayId: rel.display_id || null,
    targetId: rel.id,
    direction: rel.direction || 'outgoing',
  }));
}

export default function ItemDetails({
  selectedItemId, refreshTree, patchNode, removeNode, onApprovalChange, onNavigateToItem,
  // #817: the neighbours in the tree's VISIBLE order, or null at an end. This component
  // does not compute them — the tree owns expansion and filter state, so only the view
  // holding both can say what comes next. Null disables the button rather than wrapping.
  prevItemId = null, nextItemId = null,
}) {
  const [item, setItem] = useState(null);
  // #569: this item's own approval rollup — `{state, mine}` from
  // `GET /items/approval-states`, or `null` while loading/unknown. `state` is the
  // multi-reviewer rollup (drives the title-row badge); `mine` is the CALLER's
  // own recorded approval (drives the Approve/Unapprove button's label).
  const [approvalState, setApprovalState] = useState(null);
  // D-06 / #582 (v1): this item's DERIVED, type-scoped verification state —
  // `{state: 'verified'|'unverified'|null, basis}` from
  // `GET /items/verification-states`, or `null` while loading/unknown. Orthogonal
  // to `approvalState` above: approval is a human sign-off on the wording;
  // verification is evidence (a verifies/tests/validates trace link) that the
  // requirement is actually met. `state` itself is `null` for a non-verifiable
  // type (container, test_case, ...) — the badge below renders nothing then.
  const [verificationState, setVerificationState] = useState(null);
  // #678 v2: open when the "Record verification" control is clicked — shows existing
  // evidence records + staleness and a short add-record form (method/result/evidence).
  const [verificationDialogOpen, setVerificationDialogOpen] = useState(false);
  // F-08 / REQ-6098 (#603, D-08): open when the Approve click would supersede another
  // reviewer's live rejection, which the backend refuses without a justification.
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  // #328: `loading` is the WRITE/action-in-progress flag (Save, retype, delete,
  // archive, restore, AI tools, calculations) — it is what drives the "Saving…"
  // indicator and disables the action bar. It must NOT be raised for a plain
  // item READ: selecting another requirement was flipping this same flag, so the
  // toolbar showed "Saving…" over the *previous* item's stale content while the
  // new one loaded (the exact repro in #328). Item reads use `isFetchingItem`.
  const [loading, setLoading] = useState(false);
  const [isFetchingItem, setIsFetchingItem] = useState(false);
  const [error, setError] = useState(null);
  // #387: tab selection is tracked by KEY, not numeric index, so that reordering
  // the tabs (Comments moves to the front once an item has comments) never shifts
  // which tab is currently shown out from under the user.
  // Owner refinement (2026-07-13): until the user picks a tab, the active tab is
  // ALWAYS the FIRST one (Comments when the item has comments, else Attributes) —
  // for both leaves and containers (supersedes #342's container→Breakdown default).
  // `pickedTabKey === null` means "follow the first tab"; a manual click pins a key.
  const [pickedTabKey, setPickedTabKey] = useState(null);
  // #387: how many comments this item has — drives both the Comments tab position
  // (first when > 0, else second after Attributes) and its "(n)" count badge.
  // null = not yet known; seeded on item load, kept live by the Comments tab.
  const [commentCount, setCommentCount] = useState(null);

  // Per-item-id cache of the exported document subtree (#341: structured JSON,
  // was Markdown), owned here (not in DocumentTab) so it survives the TabPanel
  // unmounting DocumentTab on a tab switch — re-activating serves from cache.
  const documentCacheRef = useRef({});
  const getCachedDocument = useCallback(
    (id) => (Object.prototype.hasOwnProperty.call(documentCacheRef.current, id) ? documentCacheRef.current[id] : undefined),
    []
  );
  const setCachedDocument = useCallback((id, md) => { documentCacheRef.current[id] = md; }, []);

  // Same parent-owned-cache pattern for the Breakdown tab's `/breakdown`
  // payload (#342) — BreakdownTab is unmounted by TabPanel on tab switch, so a
  // component-local cache would be discarded; holding it here (ItemDetails
  // stays mounted for the life of the selected item) lets re-activating the
  // tab serve from cache instead of refetching.
  const reviewCacheRef = useRef({});
  const getCachedReview = useCallback(
    (id) => (Object.prototype.hasOwnProperty.call(reviewCacheRef.current, id) ? reviewCacheRef.current[id] : undefined),
    []
  );
  const setCachedReview = useCallback((id, data) => { reviewCacheRef.current[id] = data; }, []);

  // #238: on a narrow phone, the type-retype <Select> moves out of the crowded
  // title row into the main form (below Description) instead of duplicating it.
  const isMobile = useMediaQuery('(max-width:640px)');
  // #816: keep the status row ONE line by demoting chips as it narrows.
  // #1031: this was gated to `isMobile` on the theory that "the desktop row has
  // room" — false, and the whole reason auto-hide read as broken on desktop: at any
  // width above 640px the hook was never enabled at all, so `hidden` stayed the
  // empty Set forever regardless of how tight the row actually was. Enabled
  // unconditionally; the rule only ever hides a chip when the row is measured as
  // genuinely over width, so there is nothing for a roomy desktop row to lose.
  const chipFit = useStatusChipFit(true);

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [originalTitle, setOriginalTitle] = useState('');
  const [originalDescription, setOriginalDescription] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  // #160: title/description/attribute edits specifically — the subset of
  // `hasChanges` that network autosave is scoped to. `hasChanges` also flips
  // true for a pending relationship removal (Relationships tab X button),
  // which stays an explicit, confirm-via-Save-only action — autosave must NOT
  // fire (and re-fire on every render) purely because one is pending.
  const [fieldsDirty, setFieldsDirty] = useState(false);

  // Autosave draft + conflict state (#145).
  const [draftPrompt, setDraftPrompt] = useState(null); // a localStorage draft offered for restore
  const [conflict, setConflict] = useState(null); // 409 payload: { message, current }
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false); // #161 merge resolution
  const draftTimerRef = useRef(null);

  // UI state
  const [attributes, setAttributes] = useState([]);
  // Snapshot of attributes as loaded, so attribute edits register as changes
  // (T1.4 — otherwise hasChanges ignored attributes and Save stayed disabled).
  const [originalAttributes, setOriginalAttributes] = useState([]);
  const [relationships, setRelationships] = useState([]);
  // Meta-model fields configured to render in this main area (#196/#197),
  // for the current item's type — [] when its type has none (or none configured).
  const [mainFields, setMainFields] = useState([]);
  // The item type's configured human label (#231), e.g. "Hazard" rather than
  // the raw key — only available once the project's type list has loaded.
  const [itemTypeLabel, setItemTypeLabel] = useState(null);
  // The project's full item-type list (#197 retype UI) — every type the project has
  // enabled (system + custom), so the type-change <Select> can offer them all, not
  // just the ones with main-area fields.
  const [availableTypes, setAvailableTypes] = useState([]);
  // Relationships the user removed via the X button, pending until Save commits
  // them (bug: the X used to call the delete-link API immediately, with no undo).
  // Keyed by targetId::type::direction (index shifts as the list re-groups).
  const [pendingRelRemovals, setPendingRelRemovals] = useState(new Set());
  const [history, setHistory] = useState([]);
  // #736: the item-switch load effect below and refreshHistory() below it can both
  // be in flight at once (CommentsTab's onCountChange fires once on its own mount
  // AND again after a comment is added, and refreshHistory has no dedupe) — with no
  // sequencing, an out-of-order response (more likely under concurrent e2e-suite
  // load, where server latency variance grows) applies the OLDER fetch's rows last
  // and silently drops the just-added comment event from the tab. Guard: only the
  // most-recently-ISSUED request's response is ever committed to state.
  const historyReqIdRef = useRef(0);
  // #799: the HIGH-WATER MARK of what has actually been APPLIED to state, which is a
  // different question from what was issued last.
  //
  // The guards below used to be `reqId > historyAppliedRef.current` — only the
  // most-recently-ISSUED request may land. That silently makes the newest request a
  // single point of failure: every earlier response is discarded the moment a newer one
  // is issued, so if that newest one is slow, fails, or is starved under load, NOTHING
  // is applied and the timeline keeps stale data permanently, with no retry. Measured:
  // a history GET carrying the new comment_added event returned 200 and was thrown away
  // because a later request had already been issued.
  //
  // Comparing against the watermark instead applies any response NEWER than what is
  // currently displayed, and never regresses to an older one. Same protection against
  // out-of-order responses, without requiring the last request to be the one that wins.
  const historyAppliedRef = useRef(0);
  // #821: the mutation GENERATION. The request-id watermark answers "is this response
  // newer than what is displayed", which is not the question that matters after a write:
  // a request ISSUED BEFORE a mutation cannot contain it, however new the response is.
  //
  // MEASURED on the real stack (probe, 2026-08-10): the reported bug was not the watermark
  // and not an optimistic render. CommentsTab notifies its parent from
  // useEffect([comments]) — a RENDER — and TabPanel unmounts the inactive tab
  // (`{value === index && children}`, see the note at the top of this file about
  // BreakdownTab). Switch to History while the comment POST is still in flight and that
  // component is gone before it can re-render, so setComments no-ops, onCountChange never
  // fires, and the only post-add refresh never happens. The trace in #821 shows exactly
  // that: a history GET issued 101ms into the POST, and no further request ever.
  //
  // Two changes, because either alone leaves a hole. CommentsTab now signals from the
  // async handler at the moment the write RESOLVES, which runs whether or not the
  // component still exists. And a response records which generation it was issued under,
  // so one issued before the latest mutation can never become authoritative — that covers
  // any other in-flight write a tab activation happens to overlap.
  const historyMutationSeqRef = useRef(0);
  const [historyMutationSeq, setHistoryMutationSeq] = useState(0);
  // -1, not 0: nothing has been applied for this item yet, so the first activation must
  // fetch even though no mutation has happened (keeps #799's fetch-on-open).
  const historyAppliedMutationRef = useRef(-1);
  const noteHistoryMutation = useCallback(() => {
    historyMutationSeqRef.current += 1;
    setHistoryMutationSeq(historyMutationSeqRef.current);
  }, []);
  // One place where a history response becomes the displayed timeline.
  const applyHistoryIfNewer = useCallback((reqId, issuedAtMutation, rows) => {
    if (reqId <= historyAppliedRef.current) return;
    historyAppliedRef.current = reqId;
    historyAppliedMutationRef.current = issuedAtMutation;
    setHistory(toHistoryRows(rows));
  }, []);
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  // The fetched snapshot of the version being compared against current.
  const [compareSnapshot, setCompareSnapshot] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);

  // Dialog state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [isAddingContainer, setIsAddingContainer] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkTypeDialogOpen, setLinkTypeDialogOpen] = useState(false);
  const [linkType, setLinkType] = useState('related');
  const [selectedLinkItem, setSelectedLinkItem] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  // #347: how many direct children the item being deleted/archived has, so the
  // confirmation surfaces the cascade (archiving a parent archives its subtree).
  const [deleteChildCount, setDeleteChildCount] = useState(0);
  // In-app confirmation modal (replaces window.confirm). Holds the descriptor for
  // the pending action — { title, message, severity, confirmLabel, confirmColor,
  // testId, onConfirm } — or null when nothing is awaiting confirmation.
  const [confirm, setConfirm] = useState(null);

  // Use the AI functions hook
  const aiFeatures = useAiFunctions(selectedItemId, refreshTree, setError, setLoading);
  
  // Use the calculations hook
  const calculations = useCalculations(selectedItemId, setError, setLoading);

  // Fetch item data when selectedItemId changes
  useEffect(() => {
    // #801: un-pin the tab HERE — when the selection changes — not when the fetch
    // below resolves. The reset used to sit after `await getItem(...)`, so it landed
    // an arbitrary interval after the user could already see and use the new item's
    // panel. Anyone who clicked a tab inside that window had the click thrown away
    // when the response arrived, and the panel snapped back to the first tab.
    //
    // That is a user-facing bug on any slow connection, and it is the root cause of
    // #801's e2e flake: the spec selected an item, clicked Relationships, and the
    // in-flight fetch reset the tab out from under it — so the relationship chip it
    // was about to hover left the DOM permanently. The hover then waited out its
    // whole budget on an element that could never reattach, which is why the failure
    // looked like a hover problem and why no retry or budget change ever helped it.
    // Measured 2026-08-12: 20 004 ms of a 20 000 ms budget spent waiting for that
    // chip to attach, on a panel showing the default tab.
    //
    // The intent is unchanged (a freshly-selected item follows the FIRST tab, and a
    // manual pick within the SAME item is kept) — `selectedItemId` is this effect's
    // only dependency, so the reset still happens exactly once per selection. Same
    // shape as the comment-count / approval / verification seeds below, which all
    // clear their state synchronously before awaiting anything.
    setPickedTabKey(null);
    const loadItem = async () => {
      // Switching items: any pending debounce/conflict/draft-prompt from the
      // PREVIOUS item no longer applies.
      if (draftTimerRef.current) { clearTimeout(draftTimerRef.current); draftTimerRef.current = null; }
      setConflict(null);
      setDraftPrompt(null);

      if (!selectedItemId) {
        // Clear form if no item selected
        setItem(null);
        setTitle('');
        setDescription('');
        setAttributes([]);
        setOriginalAttributes([]);
        setRelationships([]);
        setPendingRelRemovals(new Set());
        setHistory([]);
        setHasChanges(false);
        return;
      }

      setIsFetchingItem(true);
      try {
        console.log(`Loading details for item: ${selectedItemId}`);
        // #491: fetch ONLY the item on the critical path. History is History-tab-only
        // and an unbounded set of revision rows — awaiting it in the SAME
        // Promise.allSettled as getItem meant the slower of the two gated first paint
        // (contradicting the "history is non-critical" intent below). It now loads in a
        // separate non-blocking effect (mirrors the commentCount effect).
        const data = await requirementsApi.getItem(selectedItemId);
        console.log("Item details loaded:", data);

        // Set item data
        setItem(data);
        // #150: track this as a "recently viewed" item for the command palette.
        pushRecentItem({ id: data.id, display_id: data.display_id, title: data.title });

        // Owner refinement (2026-07-13): on a freshly-selected item the tab is
        // un-pinned so it follows the FIRST tab — Comments once the count loads for a
        // commented item (which is also where a #comment- deep-link wants to land,
        // #360), else Attributes. This supersedes #342's container→Breakdown default
        // and #360's leaf-only comments special-case: both fall out of "first tab".
        // Keyed on selectedItemId so a manual tab pick within the SAME item is kept.
        //
        // #801 moved the `setPickedTabKey(null)` that used to sit here to the top of
        // this effect. Resetting on the RESPONSE discarded a tab click made while the
        // request was in flight; resetting on the SELECTION gives the same behaviour
        // without a window in which the user's click is silently undone.

        // Update form fields
        setTitle(data.title || '');
        setDescription(data.description || '');

        // Save original values for cancel functionality
        setOriginalTitle(data.title || '');
        setOriginalDescription(data.description || '');

        // Process attributes — unwrap structured {datatype,value} attrs so they
        // round-trip faithfully on save (T1.4b).
        const attrs = withRowKeys(attributesToRows(data.attributes));
        setAttributes(attrs);
        setOriginalAttributes(attrs);

        // Process relationships
        setRelationships(mapRelationships(data));
        setPendingRelRemovals(new Set());

        setHasChanges(false);
        setError(null);

        // Offer to restore a locally-saved draft left over from an unclean exit
        // (crash/closed tab) — never silently apply it (#145).
        const draft = loadDraft(selectedItemId);
        if (draft) setDraftPrompt(draft);
      } catch (err) {
        console.error('Failed to load item details:', err);
        setError(`Error loading details: ${err.message}`);
      } finally {
        setIsFetchingItem(false);
      }
    };

    loadItem();
  }, [selectedItemId]);

  // #387: seed the comment count for the freshly-selected item so the Comments
  // tab can order/badge itself before the user ever opens it. #421: this used
  // to reuse the `/breakdown` payload, which builds the item's ENTIRE subtree —
  // selecting a large container cost O(subtree) DB round trips just to badge a
  // tab. Now one O(1) count request; `/breakdown` loads only when a tab that
  // actually needs it opens (those tabs fetch-and-cache for themselves).
  // Non-blocking and best-effort: a failure just leaves the count at 0.
  useEffect(() => {
    let cancelled = false;
    setCommentCount(null);
    if (!selectedItemId) return undefined;
    (async () => {
      try {
        const data = await requirementsApi.getCommentCount(selectedItemId);
        if (!cancelled) setCommentCount((data && data.count) || 0);
      } catch {
        if (!cancelled) setCommentCount(0);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedItemId]);

  // #569: seed THIS item's own approval rollup (state + the caller's own `mine`)
  // for the title-row badge + the Approve/Unapprove button label. Same
  // non-blocking/best-effort shape as the comment-count seed above — a single
  // small `?ids=<one>` request, never the whole-subtree `/breakdown` payload.
  useEffect(() => {
    let cancelled = false;
    setApprovalState(null);
    if (!selectedItemId) return undefined;
    (async () => {
      try {
        const res = await requirementsApi.getApprovalStates([selectedItemId]);
        if (!cancelled) setApprovalState((res && res[selectedItemId]) || null);
      } catch {
        if (!cancelled) setApprovalState(null);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedItemId]);

  // D-06 / #582 (v1): seed this item's derived verification state. Same
  // non-blocking/best-effort single-id shape as the approval-state seed above.
  useEffect(() => {
    let cancelled = false;
    setVerificationState(null);
    if (!selectedItemId) return undefined;
    (async () => {
      try {
        const res = await requirementsApi.getVerificationStates([selectedItemId]);
        if (!cancelled) setVerificationState((res && res[selectedItemId]) || null);
      } catch {
        if (!cancelled) setVerificationState(null);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedItemId]);

  // N-21 / REQ-6133 (#609): live refresh for this item's OWN Breakdown tab.
  // `useReviewActivity` needs a project id, and this component's only source for
  // one is the loaded `item` itself — `GET /items/{id}` (unlike the pruned
  // breakdown/export node BreakdownTab fetches internally) carries `project_id`
  // (#197). While `item` hasn't loaded yet this is `[undefined]`, which the hook
  // treats as "nothing to watch" (filtered out), same as an empty array.
  const { changeCount: reviewChangeCount } = useReviewActivity([item?.project_id]);

  // #491: history is History-tab-only + unbounded (every revision row), so it loads OFF
  // the critical item-detail paint path — it used to be awaited in the same
  // Promise.allSettled as getItem, gating first paint on the slower of the two. Separate
  // non-blocking, best-effort effect (mirrors the commentCount effect above); the tab's
  // own re-fetch helpers still refresh it after an edit/restore.
  useEffect(() => {
    let cancelled = false;
    setHistory([]);
    // New item: nothing applied for it yet. (Ids keep increasing, so this is belt and
    // braces — but it keeps the invariant true rather than merely usually true.)
    historyAppliedRef.current = 0;
    historyAppliedMutationRef.current = -1;   // #821: nothing applied for this item yet
    if (!selectedItemId) return undefined;
    const reqId = ++historyReqIdRef.current;
    const issuedAtMutation = historyMutationSeqRef.current;
    (async () => {
      try {
        const rows = await requirementsApi.getHistory(selectedItemId);
        if (!cancelled) applyHistoryIfNewer(reqId, issuedAtMutation, rows);
      } catch (err) {
        // #799: only blank the timeline if NOTHING has successfully loaded for this item
        // yet. Under the old exact-match guard a failure here could only ever follow the
        // newest request, but the watermark makes "a later request failed after an
        // earlier one succeeded" reachable — and clearing then would destroy good data
        // because of a transient error, which is the opposite of what this branch wants.
        if (!cancelled && historyAppliedRef.current === 0) {
          console.warn('Could not load history:', err?.message);
          setHistory([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [selectedItemId, applyHistoryIfNewer]);

  // #389: the merged history feed now carries comment events too, so a comment
  // added/replied/resolved in the Comments tab must refresh History live (the
  // effect above only reloads on item switch). Best-effort, same-item.
  // #736: DOES need the same sequencing guard as the effect above — CommentsTab
  // fires this once on its own mount (baseline count) and again after an add, so
  // two requests can be in flight together; only the response for the most
  // recently issued request may land in state (see historyReqIdRef comment).
  const refreshHistory = useCallback(async () => {
    if (!selectedItemId) return;
    const reqId = ++historyReqIdRef.current;
    // #821: read the generation at ISSUE time. A mutation landing after this point leaves
    // historyAppliedMutationRef behind, and the effect below issues a fresh request.
    const issuedAtMutation = historyMutationSeqRef.current;
    try {
      const rows = await requirementsApi.getHistory(selectedItemId);
      applyHistoryIfNewer(reqId, issuedAtMutation, rows);
    } catch (err) {
      console.warn('Could not refresh history:', err?.message);
    }
  }, [selectedItemId, applyHistoryIfNewer]);

  // Track changes - robust implementation to avoid false positives
  useEffect(() => {
    // Only consider changes once an item is loaded. Gate on `item` (not just
    // originalTitle/Description) so attribute-only edits are tracked even when
    // the title/description happen to be empty (T1.4).
    if (item) {
      // Normalize strings for comparison to avoid false positives. Strip HTML
      // via the shared htmlToText() util (regex-based, never assigns untrusted
      // markup to a live-document element) — assigning the raw description to a
      // detached div's innerHTML fired resource-load handlers (<img onerror>),
      // a stored-XSS → token-theft sink (SR-1). Collapse whitespace so the
      // comparison stays insensitive to formatting-only differences.
      const normalizeHtml = (html) => htmlToText(html).replace(/\s+/g, ' ').trim();

      const normalizedTitle = title.trim();
      const normalizedOriginalTitle = originalTitle.trim();
      const normalizedDesc = normalizeHtml(description);
      const normalizedOriginalDesc = normalizeHtml(originalDescription);
      
      // Compare normalized versions
      const titleChanged = normalizedTitle !== normalizedOriginalTitle;
      const descriptionChanged = normalizedDesc !== normalizedOriginalDesc;
      // Attribute edits (add/edit/delete) must also count as changes so the
      // Save button enables and they can persist (T1.4).
      const attrChanged = attributesChanged(attributes, originalAttributes);
      const changed = titleChanged || descriptionChanged || attrChanged || pendingRelRemovals.size > 0;
      setFieldsDirty(titleChanged || descriptionChanged || attrChanged);

      // For debugging, log values only when change state would actually change
      const wouldChangeState = changed !== hasChanges;
      if (wouldChangeState) {
        console.log('Change detection details:', {
          hasChanges: changed,
          titleChanged,
          descriptionChanged,
          attributesChanged: attrChanged,
          title: `"${normalizedTitle}"`, 
          originalTitle: `"${normalizedOriginalTitle}"`,
          normalizedDesc: `"${normalizedDesc.substring(0, 50)}${normalizedDesc.length > 50 ? '...' : ''}"`,
          normalizedOriginalDesc: `"${normalizedOriginalDesc.substring(0, 50)}${normalizedOriginalDesc.length > 50 ? '...' : ''}"`,
          descLength: normalizedDesc.length,
          origDescLength: normalizedOriginalDesc.length
        });
      }
      
      setHasChanges(changed);
    } else {
      // No item loaded yet
      setHasChanges(false);
      setFieldsDirty(false);
    }
  }, [title, description, originalTitle, originalDescription, attributes, originalAttributes, item, hasChanges, pendingRelRemovals]);

  // Debounced local draft autosave (#145) — a crash/closed-tab safety net, NOT
  // a replacement for the real Save. Only persists while there are unsaved
  // edits on a loaded item; cleared on successful save/cancel/discard.
  useEffect(() => {
    if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    if (!item || !hasChanges || !selectedItemId) return undefined;
    draftTimerRef.current = setTimeout(() => {
      saveDraft(selectedItemId, { title, description, attributes });
    }, 800);
    return () => clearTimeout(draftTimerRef.current);
  }, [selectedItemId, item, hasChanges, title, description, attributes]);

  // Network autosave + offline queue/retry (#160). Builds the same payload
  // shape handleSave sends (title/description/attributes + the loaded
  // `version` precondition), re-created fresh on every relevant edit so the
  // hook's internal debounce resets on each keystroke (same idiom as
  // useDebouncedValue). Deliberately excludes pending relationship removals —
  // those stay an explicit Save-button action (see fieldsDirty above).
  const getAutosavePayload = useCallback(() => {
    if (!item) return null;
    return {
      apiPayload: { ...item, title, description, attributes: rowsToAttributes(attributes) },
      formSnapshot: { title, description, attributes },
    };
  }, [item, title, description, attributes]);

  // Refs mirroring the ORIGINAL* values every render, read inside
  // handleAutosaved below to tell apart two cases on a completed background
  // save: (a) the visible form still shows exactly the pre-save baseline (the
  // common case, and also the "flushed a queued offline edit after a reload"
  // case — the visible fields never picked up the queued edit at all) — safe
  // to advance the visible fields to what was just saved; vs. (b) the user
  // has since typed something newer than what this save sent — must NOT
  // clobber that with the (now stale) snapshot this save was carrying.
  const originalTitleRef = useRef(originalTitle);
  originalTitleRef.current = originalTitle;
  const originalDescriptionRef = useRef(originalDescription);
  originalDescriptionRef.current = originalDescription;
  const originalAttributesRef = useRef(originalAttributes);
  originalAttributesRef.current = originalAttributes;

  // #567: Save/autosave/conflict-merge used to call `refreshTree()` unconditionally
  // — a full reload of the whole top level PLUS every expanded branch (one request
  // per open branch), on EVERY save including every autosave cycle while the user
  // was mid-edit (the reported "tree reloads continuously"). `patchNode` splices
  // just this one node in place, no network request; falls back to `refreshTree`
  // when a caller doesn't pass it (older/test call sites), so behaviour is
  // unchanged there. `removeNode` is Delete's equivalent.
  const safePatchNode = useCallback((id, updater) => {
    if (patchNode) patchNode(id, updater);
    else if (refreshTree) refreshTree();
  }, [patchNode, refreshTree]);
  const safeRemoveNode = useCallback((id) => {
    if (removeNode) removeNode(id);
    else if (refreshTree) refreshTree();
  }, [removeNode, refreshTree]);

  // On a successful autosave, adopt the server's response the same way
  // handleSave does (merge item so `version` advances) and reset the
  // ORIGINAL* values to exactly what was sent — not "whatever is current
  // now", which may have moved on if the user kept typing during the
  // round trip (that later edit correctly stays flagged dirty afterward).
  const handleAutosaved = useCallback((saved, formSnapshot) => {
    setItem((prev) => ({ ...prev, ...saved }));
    // #567: only the TITLE is rendered in a tree row — patch it in place, and
    // only when it actually changed since the pre-save baseline (a
    // description/attribute-only autosave touches the tree not at all, per the
    // reported ask: "in many cases the title would not change and therefore
    // there would be no need to reload the node at all").
    if (formSnapshot && selectedItemId && formSnapshot.title !== originalTitleRef.current) {
      const newTitle = formSnapshot.title;
      safePatchNode(selectedItemId, (n) => ({ ...n, title: newTitle }));
    }
    if (formSnapshot) {
      // See the refs' comment above: only advance the VISIBLE field if it
      // still matches the pre-save baseline — never overwrite a newer,
      // still-unsaved keystroke made during the round trip.
      setTitle((prev) => (prev === originalTitleRef.current ? formSnapshot.title : prev));
      setDescription((prev) => (prev === originalDescriptionRef.current ? formSnapshot.description : prev));
      // #943: formSnapshot.attributes can come from a QUEUED offline save
      // (dequeueSave, restored from localStorage — see offlineQueue.js), which
      // may predate `_key` or have been written by attributesToRows(...)
      // directly — re-stamp defensively rather than trust the snapshot already
      // carries React `key=` identities.
      const snapAttrs = withRowKeys(formSnapshot.attributes);
      setAttributes((prev) => (attributesChanged(prev, originalAttributesRef.current) ? prev : snapAttrs));
      setOriginalTitle(formSnapshot.title);
      setOriginalDescription(formSnapshot.description);
      setOriginalAttributes(withRowKeys(formSnapshot.attributes));
    }
    if (selectedItemId) clearDraft(selectedItemId); // the real write landed — the local safety net is moot
  }, [selectedItemId, safePatchNode]);

  // A 409 during autosave is the same "someone else saved this item" case
  // handleSave already handles — reuse the exact same conflict banner/UI
  // instead of a second, competing surface.
  const handleAutosaveConflict = useCallback((detail) => { setConflict(detail); }, []);

  const autosave = useAutosave({
    itemId: selectedItemId,
    // Pause autosave while: a manual Save is already in flight (avoid a
    // double-submit race), a 409 conflict is unresolved (re-firing would just
    // 409 again), or a leftover draft is still awaiting restore/discard.
    dirty: fieldsDirty && !loading && !conflict && !draftPrompt,
    getPayload: getAutosavePayload,
    onSaved: handleAutosaved,
    onConflict: handleAutosaveConflict,
  });

  // Load this item's type's meta-model fields configured to show in the main
  // area (#196/#197) — [] silently when the item has no project/type or none
  // of its type's fields are so configured (not every item type needs this).
  useEffect(() => {
    let cancelled = false;
    if (!item?.project_id || !item?.type_id) { setMainFields([]); setItemTypeLabel(null); setAvailableTypes([]); return undefined; }
    iamApi.projectItemTypes(item.project_id)
      .then((types) => {
        if (cancelled) return;
        const type = (types || []).find((t) => t.id === item.type_id);
        setMainFields((type?.fields || []).filter((f) => f.show_in_main));
        setItemTypeLabel(type?.label || null);
        setAvailableTypes(types || []);
      })
      .catch(() => { if (!cancelled) { setMainFields([]); setItemTypeLabel(null); setAvailableTypes([]); } }); // non-critical — main area just stays empty
    return () => { cancelled = true; };
  }, [item?.project_id, item?.type_id]);

  // Read/write a main-area field's value in the attribute-rows array (shared
  // storage with the Attributes tab — same rows, same save path).
  const getFieldValue = (key) => {
    const row = attributes.find((a) => a && a.name === key);
    return row ? row.value : '';
  };
  const setFieldValue = (key, value) => {
    setAttributes((prev) => {
      const idx = prev.findIndex((a) => a && a.name === key);
      if (idx === -1) return [...prev, { name: key, type: 'string', value, structured: false, _key: uuidv4() }];
      return prev.map((a, i) => (i === idx ? { ...a, value } : a));
    });
  };

  const handleRestoreDraft = () => {
    if (!draftPrompt) return;
    setTitle(draftPrompt.title || '');
    setDescription(draftPrompt.description || '');
    setAttributes(draftPrompt.attributes || []);
    setDraftPrompt(null);
  };

  const handleDiscardDraft = () => {
    if (selectedItemId) clearDraft(selectedItemId);
    setDraftPrompt(null);
  };

  // #817: one navigation path for the buttons, the keys and (later) the swipe, so the
  // unsaved-changes guard cannot be implemented three times and drift. Confirming
  // NAVIGATES AWAY AND LOSES THE EDIT, which is what the prompt has to say — this view
  // autosaves a draft but the item itself is not written, and "OK" on a vague prompt is
  // how people lose work.
  const navigateToNeighbour = useCallback((targetId) => {
    if (!targetId) return;
    if (hasChanges) {
      // #943: the guard is the in-app modal now, not a native window.confirm. The
      // navigation is deferred into `onConfirm`; dismissing the modal navigates
      // nowhere, which is what cancelling the native prompt did.
      setConfirm({
        title: 'Unsaved changes',
        message: 'This item has unsaved changes. Leave it and lose them?',
        severity: 'warning',
        confirmLabel: 'Leave and lose them',
        testId: 'itemdetails-unsaved-changes-confirm',
        onConfirm: () => onNavigateToItem?.(targetId),
      });
      return;
    }
    onNavigateToItem?.(targetId);
  }, [hasChanges, onNavigateToItem]);

  // #817: swipe, mobile only. Routed through the SAME navigateToNeighbour as the
  // buttons and the keys, so the unsaved-changes prompt cannot be skipped by the one
  // input that is hardest to undo on a phone.
  const onSwipe = useCallback((dir) => {
    navigateToNeighbour(dir === 'prev' ? prevItemId : nextItemId);
  }, [navigateToNeighbour, prevItemId, nextItemId]);
  const swipeRef = useSwipeNavigate(isMobile, onSwipe);

  // #817: Alt+← / Alt+→ on desktop. Alt rather than a bare arrow because the arrows
  // belong to whatever the user is editing; and skipped entirely while focus is in a
  // text field or a contenteditable, where Alt+arrow is a word-wise caret move on some
  // platforms. Nothing is bound on touch — the swipe is the mobile affordance.
  useEffect(() => {
    const onKey = (e) => {
      if (!e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      const el = e.target;
      const tag = el?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el?.isContentEditable) return;
      const target = e.key === 'ArrowLeft' ? prevItemId : nextItemId;
      if (!target) return;   // at an end: let the key fall through untouched
      e.preventDefault();
      navigateToNeighbour(target);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [prevItemId, nextItemId, navigateToNeighbour]);

  // Add unsaved changes warning for page unload
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasChanges) {
        // Standard message displayed by the browser
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasChanges]);

  const handleSave = async () => {
    if (!selectedItemId || !item) return;

    // #654 (1): the AttributesTab already flags a colliding name inline, but
    // that's a warning, not a block — refuse the save outright rather than
    // silently dropping one attribute's data via plain-object "last key wins".
    const dupes = duplicateAttributeNames(attributes);
    if (dupes.size > 0) {
      setError(`Fix duplicate attribute name(s) before saving: ${[...dupes].join(', ')}`);
      return;
    }

    setLoading(true);
    setConflict(null);
    try {
      // Prepare updated data. `...item` already carries `version` (#159) — the
      // precondition the backend checks to detect a concurrent stale write.
      const updatedData = {
        ...item,
        title,
        description
      };

      // Convert attribute rows back to the stored object, preserving structured
      // {datatype,value} attrs and coercing plain values to their type (T1.4b).
      updatedData.attributes = rowsToAttributes(attributes);

      // Save to API — capture the response so `item.version` advances (#159),
      // otherwise every save after the first would race on a stale version.
      const saved = await requirementsApi.updateItem(selectedItemId, updatedData);
      // Merge (not replace) — resilient to a caller/response that omits fields
      // (e.g. a partial mock), while still picking up the new `version`.
      setItem((prev) => ({ ...prev, ...saved }));

      // Update original values (incl. attributes) so hasChanges resets (T1.4)
      setOriginalTitle(title);
      setOriginalDescription(description);
      setOriginalAttributes(attributes);
      clearDraft(selectedItemId); // the real save landed — the local safety net is moot
      dequeueSave(selectedItemId); // #160: this manual save supersedes any still-queued offline edit

      // Commit any pending relationship removals (the X button pends until Save,
      // per the reported bug — it used to delete the link instantly with no undo).
      if (pendingRelRemovals.size > 0) {
        const toRemove = relationships.filter((rel) => pendingRelRemovals.has(relKey(rel)));
        for (const rel of toRemove) {
          try {
            await requirementsApi.deleteLink(selectedItemId, rel.targetId, rel.type);
          } catch (linkErr) {
            console.error('Failed to remove relationship:', rel, linkErr);
          }
        }
        setPendingRelRemovals(new Set());
        try {
          const refreshed = await requirementsApi.getItem(selectedItemId);
          setRelationships(mapRelationships(refreshed));
        } catch {
          // Non-critical — the tab still reflects the pending state cleared.
        }
      }

      setHasChanges(false);

      // #567: patch only the TITLE in the tree, and only if it actually changed —
      // a description/attribute-only save doesn't touch the tree row at all (see
      // handleAutosaved's identical rule for why: nothing there is rendered by
      // RequirementsTree, so there is nothing to reload).
      if (title !== originalTitle) {
        safePatchNode(selectedItemId, (n) => ({ ...n, title }));
      }

      // Refresh the version history so the new revision shows immediately.
      // #736: same sequencing guard as refreshHistory — don't let this response
      // land after a later one (e.g. a concurrent comment-driven refresh).
      try {
        noteHistoryMutation();   // #821: this write produced history events
        const reqId = ++historyReqIdRef.current;
        const issuedAtMutation = historyMutationSeqRef.current;
        const hist = await requirementsApi.getHistory(selectedItemId);
        applyHistoryIfNewer(reqId, issuedAtMutation, hist);
      } catch {
        // Non-critical.
      }

    } catch (err) {
      // #159/#145: a stale write (someone else saved this item first) is a
      // conflict to resolve, not a generic error — keep the user's edits and
      // let them choose Reload vs. Overwrite instead of silently failing.
      if (err.response?.status === 409 && err.response?.data?.detail) {
        setConflict(err.response.data.detail);
      } else {
        setError(`Failed to update: ${err.message}`);
      }
      console.error('Failed to update item:', err);
    } finally {
      setLoading(false);
    }
  };

  // Retype an item post-creation (#197 recovery path) — an immediate action,
  // separate from the title/description/attributes Save flow (it doesn't touch
  // `hasChanges`/the draft autosave), but still honours the same optimistic-
  // concurrency `version` precondition: it PUTs the item's own last-loaded
  // title/description/attributes/version, only overriding `type`, and reuses the
  // exact same 409-conflict handling as Save.
  const handleRetype = async (newTypeKey) => {
    if (!selectedItemId || !item || !newTypeKey) return;
    const currentTypeKey = availableTypes.find((t) => t.id === item.type_id)?.key;
    if (newTypeKey === currentTypeKey) return;
    setLoading(true);
    setConflict(null);
    try {
      const saved = await requirementsApi.updateItem(selectedItemId, {
        ...item,
        type: newTypeKey,
      });
      // #197: the main-area fields effect keys off `item.type_id` to know when
      // to re-fetch — force it to the NEW type's id explicitly rather than
      // trusting `saved` alone to carry it. Belt-and-braces: if the update
      // response ever omits/lags `type_id` (e.g. a partial DTO), the displayed
      // attribute fields would silently keep showing the OLD type's fields
      // instead of reloading, which is exactly the gap that was reported.
      const newType = availableTypes.find((t) => t.key === newTypeKey);
      setItem((prev) => ({ ...prev, ...saved, ...(newType ? { type_id: newType.id } : {}) }));
      if (refreshTree) refreshTree();
    } catch (err) {
      if (err.response?.status === 409 && err.response?.data?.detail) {
        setConflict(err.response.data.detail);
      } else {
        setError(`Failed to change item type: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Conflict resolution (#159/#145) ------------------------------------------
  const handleConflictReload = async () => {
    setConflict(null);
    clearDraft(selectedItemId);
    dequeueSave(selectedItemId); // #160: discarding local edits also drops any queued offline write
    await reloadItemAndHistory();
  };

  const handleConflictOverwrite = () => {
    // Adopt the current server version as our new precondition, then let the
    // NEXT Save (unchanged title/description/attributes from this form) apply
    // on top of it — this is the "overwrite" choice, made explicit rather than
    // happening silently.
    if (conflict?.current) setItem((prev) => (prev ? { ...prev, version: conflict.current.version } : prev));
    setConflict(null);
  };

  // #161: the third conflict-resolution option — merge, picking mine/theirs
  // per field (ConflictMergeDialog) rather than an all-or-nothing whole-version
  // choice. Saves immediately against the server's current version (the same
  // precondition Overwrite adopts) so the merged result is what lands, not a
  // dangling local edit waiting on a second manual Save.
  const handleConflictMerge = async (mergedFields) => {
    if (!selectedItemId || !conflict?.current) return;
    setLoading(true);
    try {
      const updatedData = {
        ...item,
        title: mergedFields.title,
        description: mergedFields.description,
        attributes: rowsToAttributes(attributes),
        version: conflict.current.version,
      };
      const saved = await requirementsApi.updateItem(selectedItemId, updatedData);
      setItem((prev) => ({ ...prev, ...saved }));
      setTitle(mergedFields.title);
      setDescription(mergedFields.description);
      setOriginalTitle(mergedFields.title);
      setOriginalDescription(mergedFields.description);
      setOriginalAttributes(attributes);
      setHasChanges(false);
      clearDraft(selectedItemId);
      dequeueSave(selectedItemId);
      setConflict(null);
      setMergeDialogOpen(false);
      // #567: same title-only patch rule as handleSave/handleAutosaved.
      if (mergedFields.title !== originalTitle) {
        const newTitle = mergedFields.title;
        safePatchNode(selectedItemId, (n) => ({ ...n, title: newTitle }));
      }
      // #736: sequencing guard — see historyReqIdRef comment.
      try {
        noteHistoryMutation();   // #821: this write produced history events
        const reqId = ++historyReqIdRef.current;
        const issuedAtMutation = historyMutationSeqRef.current;
        const hist = await requirementsApi.getHistory(selectedItemId);
        applyHistoryIfNewer(reqId, issuedAtMutation, hist);
      } catch {
        // Non-critical.
      }
    } catch (err) {
      // A second concurrent write landed while the merge dialog was open — hand
      // back to the SAME conflict banner (now against the newer `current`)
      // rather than swallowing it as a generic error.
      if (err.response?.status === 409 && err.response?.data?.detail) {
        setConflict(err.response.data.detail);
        setMergeDialogOpen(false);
      } else {
        setError(`Failed to update: ${err.message}`);
      }
      console.error('Failed to merge item:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setTitle(originalTitle);
    setDescription(originalDescription);
    setAttributes(originalAttributes);
    setPendingRelRemovals(new Set());
    setHasChanges(false);
    if (selectedItemId) {
      clearDraft(selectedItemId);
      dequeueSave(selectedItemId); // #160: reverting edits also drops any queued offline write
    }
  };

  const handleDelete = async () => {
    if (!selectedItemId) return;

    // #347/#658: look up the LIVE descendant count (whole subtree, not just
    // direct children) so the dialog's warning is accurate — Delete now
    // actually cascades to every one of them (#658), so undercounting here
    // would understate what's about to happen. Best-effort — never block the
    // dialog on it.
    try {
      const impact = await requirementsApi.getArchiveImpact(selectedItemId);
      setDeleteChildCount(impact?.live_descendant_count || 0);
    } catch {
      setDeleteChildCount(0);
    }

    // Open the delete confirmation dialog instead of using window.confirm
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async (reason) => {
    setDeleteDialogOpen(false);

    if (!selectedItemId) return;

    setLoading(true);
    try {
      // Call the delete API (reason recorded in the audit log of linked items)
      await requirementsApi.deleteItem(selectedItemId, reason);
      console.log('Item deleted successfully:', selectedItemId);

      // Clear the form since the item no longer exists
      setItem(null);
      setTitle('');
      setDescription('');
      setAttributes([]);
      setRelationships([]);
      setHistory([]);

      // #567: remove just this ONE node from the tree instead of a full reload.
      safeRemoveNode(selectedItemId);

    } catch (err) {
      setError(`Failed to delete: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  // #193 human-in-the-loop archive: an agent can only PROPOSE an archive (MCP
  // `archive_requirement`) — the item stays fully visible with
  // attributes.status === 'archive_proposed' until a human confirms or cancels here
  // (REST-only endpoints; no MCP tool can reach them).
  const doConfirmArchive = async () => {
    if (!selectedItemId) return;
    setLoading(true);
    try {
      await requirementsApi.confirmArchive(selectedItemId);
      // The item is now actually archived (soft-deleted) — same as Delete: clear the
      // form since it's no longer in the active tree.
      setItem(null);
      setTitle('');
      setDescription('');
      setAttributes([]);
      setRelationships([]);
      setHistory([]);
      if (refreshTree) refreshTree();
    } catch (err) {
      setError(`Failed to confirm archive: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  const handleConfirmArchive = async () => {
    if (!selectedItemId) return;
    // #347/#658: confirming archive on a parent cascades to its WHOLE live
    // subtree (not just direct children) — surface the real count in an
    // in-app modal (not a native alert) before proceeding. Best-effort
    // lookup: never block the archive on it.
    try {
      const impact = await requirementsApi.getArchiveImpact(selectedItemId);
      const n = impact?.live_descendant_count || 0;
      if (n > 0) {
        setConfirm({
          title: 'Archive this item and its children?',
          message: `This action will also archive all ${n} child item${n === 1 ? '' : 's'}.`,
          severity: 'warning', confirmColor: 'error', confirmLabel: 'Archive all',
          testId: 'archive-cascade-dialog',
          onConfirm: doConfirmArchive,
        });
        return;
      }
    } catch { /* fall through to archive */ }
    await doConfirmArchive();
  };

  // Load a server copy of the item into the form (after a REST action changed it).
  const applyServerItem = (data) => {
    setItem(data);
    setTitle(data.title || '');
    setDescription(data.description || '');
    setOriginalTitle(data.title || '');
    setOriginalDescription(data.description || '');
    const attrs = withRowKeys(attributesToRows(data.attributes));
    setAttributes(attrs);
    setOriginalAttributes(attrs);
    if (refreshTree) refreshTree();
  };

  const handleCancelArchive = async () => {
    if (!selectedItemId) return;
    setLoading(true);
    try {
      const data = await requirementsApi.cancelArchive(selectedItemId);
      applyServerItem(data);
    } catch (err) {
      setError(`Failed to cancel archive proposal: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // #569: Approve / Unapprove for a leaf requirement — replaces the old
  // Verify/Unverify button (#364), which just set a free-text `status`
  // attribute. Writes the CALLER's own approval record via the existing
  // `PUT /items/{id}/approval` (`item_approvals` — real reviewer identity,
  // content-hash staleness, multi-reviewer rollup), the same endpoint the
  // Breakdown tab's ApprovePill already uses. Backend `review`-or-`edit`
  // permission is enforced server-side (`require_review_permission`), same
  // as Save/Delete gate purely on the backend.
  //
  // #567: no tree/network refetch of any kind here — approvals live in
  // `item_approvals`, never the item row, so there is nothing on the item
  // itself to reload. The one small `?ids=<this item>` re-fetch below gets
  // the fresh ROLLUP (considering every reviewer + the current content hash,
  // which the PUT response alone doesn't carry — it returns just the single
  // saved row, or `{cleared: true}`) and is handed to the caller's
  // `approvalStates` map via `onApprovalChange`, not used to reload anything.
  const doToggleApproval = async (extra) => {
    setLoading(true);
    try {
      const nextState = approvalState?.mine === 'approved' ? null : 'approved';
      // Only pass the third argument when there IS one (same rule as BreakdownTab):
      // an explicit `undefined` is noise on the call and silently changes its arity.
      if (extra) await requirementsApi.setApproval(selectedItemId, nextState, extra);
      else await requirementsApi.setApproval(selectedItemId, nextState);
      const fresh = await requirementsApi.getApprovalStates([selectedItemId]);
      // NOT `|| { state: 'pending', mine: null }`. The write above SUCCEEDED — so if
      // the confirming re-read comes back empty (a transient failure, or an id the
      // batch declines to answer for), falling back to "pending" tells the user
      // their approval did not take, seconds after it did. Keep what is actually
      // known: the decision just written. It is the caller's own, so it is `mine`.
      const rec = (fresh && fresh[selectedItemId]) || { state: nextState, mine: nextState };
      setApprovalState(rec);
      onApprovalChange?.(selectedItemId, rec);
    } catch (err) {
      setError(`Failed to update approval: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // #678 v2: after the dialog records a new evidence record, re-fetch this item's
  // rollup (same single-id shape the approval re-fetch above uses) so the title-row
  // badge reflects `effective_state` immediately — no page reload / tab switch needed.
  const handleVerificationRecorded = async () => {
    if (!selectedItemId) return;
    try {
      const fresh = await requirementsApi.getVerificationStates([selectedItemId]);
      if (fresh && fresh[selectedItemId]) setVerificationState(fresh[selectedItemId]);
    } catch {
      // Best-effort refresh — the record itself already succeeded (the dialog stays
      // open and shows it in its own list); a failed re-fetch here just means the
      // title-row badge updates on the next natural reload instead of instantly.
    }
  };

  // F-08 / REQ-6098 (#603, D-08): approving an item that carries ANOTHER reviewer's live
  // rejection supersedes it, and the backend refuses that write without a justification
  // (400) — so this button had exactly the failure the Breakdown pill had before the
  // dialog landed: the click round-tripped, 400ed, and surfaced as a bare error toast.
  // The rollup is enough to detect it: `state === 'not_approved'` means SOMEONE has a
  // live rejection, and `mine !== 'not_approved'` means it is not this caller's own
  // (clearing your own rejection is not an override and needs nothing).
  const approvalWouldOverride = approvalState?.state === 'not_approved'
    && approvalState?.mine !== 'not_approved'
    && approvalState?.mine !== 'approved';

  const handleToggleApproval = async () => {
    if (!selectedItemId || !item) return;
    if (approvalWouldOverride) { setOverrideDialogOpen(true); return; }
    await doToggleApproval();
  };

  // #387/#1045: each rendered `<Tab>` carries its own stable KEY as its MUI
  // `value` (set explicitly below, not left to MUI's default of "position in the
  // strip") — so `newKey` here IS the picked tab's key already, straight from the
  // element that was actually clicked. Nothing is translated through `tabOrder`
  // at click time any more.
  //
  // That translation is exactly what #1045 broke: the old code read MUI's
  // POSITIONAL index (`newValue`) and looked it up in `tabOrder[newValue]`. A
  // click that lands (or is still resolving) while the comment-count fetch
  // reorders the strip underneath it can have that index point at the wrong
  // tab, or past the end of a strip that has since changed length — and the old
  // fallback (`|| 'attributes'`) then silently pinned Attributes instead of
  // refusing the click or keeping the previous pick. Reading the key straight
  // off the clicked element removes the index arithmetic (and that fallback)
  // entirely, so there is nothing left for a reorder to invalidate.
  const handleTabChange = (e, newKey) => {
    // A manual click PINS the chosen tab (overriding the default "follow the
    // first tab" behaviour) until the user selects a different item.
    setPickedTabKey(newKey);
  };

  const handleEditorChange = (content) => {
    setDescription(content);
  };

  // Restore the item to a previous version (records a new 'restore' revision).
  const reloadItemAndHistory = async () => {
    const data = await requirementsApi.getItem(selectedItemId);
    setItem(data);
    setTitle(data.title || '');
    setDescription(data.description || '');
    setOriginalTitle(data.title || '');
    setOriginalDescription(data.description || '');
    const attrs = withRowKeys(attributesToRows(data.attributes));
    setAttributes(attrs);
    setOriginalAttributes(attrs);
    // A restore can change relationships too (restore_version reconciles links
    // back to the target version's snapshot) — without this the tab kept
    // showing the pre-restore list until the item was reloaded some other way.
    setRelationships(mapRelationships(data));
    setPendingRelRemovals(new Set());
    setHasChanges(false);
    // #736: sequencing guard — see historyReqIdRef comment.
    try {
      noteHistoryMutation();   // #821: a restore produces history events
      const reqId = ++historyReqIdRef.current;
      const issuedAtMutation = historyMutationSeqRef.current;
      const hist = await requirementsApi.getHistory(selectedItemId);
      applyHistoryIfNewer(reqId, issuedAtMutation, hist);
    } catch { /* non-critical */ }
  };

  const doRestoreVersion = async (version) => {
    setLoading(true);
    try {
      await requirementsApi.restoreVersion(selectedItemId, version);
      await reloadItemAndHistory();
      if (refreshTree) refreshTree();
      setError(null);
    } catch (err) {
      setError(`Failed to restore version ${version}: ${err.message}`);
      console.error('Failed to restore version:', err);
    } finally {
      setLoading(false);
    }
  };
  const handleRestoreVersion = (version) => {
    if (!selectedItemId) return;
    // In-app confirmation (replaces window.confirm).
    setConfirm({
      title: `Restore to version ${version}?`,
      message: `The current content will be saved as a new revision, then version ${version} will be restored.`,
      confirmLabel: 'Restore',
      testId: 'restore-version-dialog',
      onConfirm: () => doRestoreVersion(version),
    });
  };

  // Handle version comparison — fetch the historical snapshot and show a
  // side-by-side diff against the item's current content.
  const handleCompareVersion = async (version) => {
    setSelectedVersion(version);
    setCompareSnapshot(null);
    setCompareDialogOpen(true);
    setCompareLoading(true);
    try {
      const snapshot = await requirementsApi.getHistoryVersion(selectedItemId, version);
      setCompareSnapshot(snapshot);
    } catch (err) {
      console.error('Failed to load version for comparison:', err);
      setError(`Could not load version ${version}: ${err.message}`);
    } finally {
      setCompareLoading(false);
    }
  };

  // Handle relationship functionality
  const handleLink = () => {
    // Open the UnifiedContainerSelector dialog
    setLinkDialogOpen(true);
  };

  const handleLinkSubmit = async (targetId, targetName) => {
    if (!targetId || !selectedItemId) return;
    
    setLoading(true);
    try {
      console.log(`Creating link: ${selectedItemId} -> ${targetId} (${linkType})`);
      
      // Make API call to create link
      await requirementsApi.createLink(selectedItemId, targetId, linkType);
      
      // Show success message
      setError(`Linked successfully to ${targetName}`, 'success');
      
      // Close dialog
      setLinkDialogOpen(false);
      
      // Refresh item data to show new relationship
      const data = await requirementsApi.getItem(selectedItemId);
      setRelationships(mapRelationships(data));

    } catch (err) {
      setError(`Failed to create link: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Marks a relationship for removal on the next Save (does NOT call the API
  // immediately) — the X button used to delete the link instantly, with no
  // confirm and no undo. Toggling the same key again (the RelationshipsTab
  // "Undo" affordance) un-marks it.
  const handleDeleteRelationship = (rel) => {
    if (!rel.targetId) return;
    setPendingRelRemovals((prev) => {
      const next = new Set(prev);
      const k = relKey(rel);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  // Add handler for opening the dialog
  const handleOpenAddDialog = (isContainer = false) => {
    setIsAddingContainer(isContainer);
    setAddDialogOpen(true);
  };

  // Add handler for closing the dialog
  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
  };

  // Add handler for adding new item/container
  const handleAddItem = async (newItem) => {
    setLoading(true);
    try {
      // Create the new item/container
      await requirementsApi.createItem(newItem);

      // Refresh the tree
      if (refreshTree) refreshTree();

      // Close the dialog
      handleCloseAddDialog();
    } catch (err) {
      setError(`Failed to create: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Status badge for the title row — derived from a status/verification
  // attribute, mirroring the tree's status dot (mockup .titlerow .badge).
  const STATUS_TONES = {
    pass: 'success', passed: 'success', complete: 'success', approved: 'success',
    draft: 'warning', pending: 'warning', review: 'warning', partial: 'warning',
    fail: 'error', failed: 'error', failing: 'error', rejected: 'error', blocked: 'error',
    // #193: an agent-proposed archive is a distinct, human-actionable state — must NOT
    // read as just another "draft"/warning. Reuses the `error` tone (already red, see
    // ds.css --error) rather than inventing a new colour that would visually compete.
    archive_proposed: 'error',
  };
  // F-21 / REQ-6107 (#581): `verified`/`unverified` are the RETIRED free-text
  // concept #569 replaced with the `item_approvals` rollup (`approvalBadge`,
  // below) — an item whose `status` attribute still carries a historical
  // 'verified'/'unverified' value (written by the now-deleted setVerified()/
  // "Verify all") must NOT also render as a second, disagreeing pill right next
  // to the real approval badge (found in the #581 audit: "a stale Verified pill
  // beside the new badge — two disagreeing verdicts"). Every OTHER value this
  // attribute legitimately carries for other item types (pass/fail/draft/...,
  // #581 audit's "verified clean" list) still renders.
  const RETIRED_STATUS_VALUES = new Set(['verified', 'unverified']);
  const statusBadge = (() => {
    const a = (attributes || []).find((x) => x && /^(status|verification[_ ]?status)$/i.test(String(x.name || '').trim()));
    const raw = a ? String(a.value || '').trim() : '';
    if (!raw || RETIRED_STATUS_VALUES.has(raw.toLowerCase())) return null;
    const tone = STATUS_TONES[raw.toLowerCase()] || 'neutral';
    const label = raw.replace(/_/g, ' ');
    return { tone, label: label.charAt(0).toUpperCase() + label.slice(1) };
  })();

  // #569: the title-row APPROVAL badge (distinct from `statusBadge` above, which
  // stays keyed to the free-text status/verification_status attribute for
  // whatever OTHER per-type meaning it still carries — e.g. a test case's
  // pass/fail — now that the dot/approve-button no longer read it). Sourced from
  // `approvalState` (fetched per-item, see the effect above): `null` while
  // loading, hidden for `pending` (nothing decided yet — matches the tree's "no
  // dot" rule), shown otherwise with the SAME tone convention as the tree dot /
  // Breakdown tab's ApprovePill (approved=success, not_approved=error,
  // stale=warning).
  // F-11 / REQ-6101 (#603): `stale_state` says WHICH decision lapsed, so this badge no
  // longer reads "Approved · stale" over a lapsed rejection.
  const approvalBadge = approvalState && approvalState.state !== 'pending'
    ? approvalToneOf(approvalState.state, approvalState.stale_state)
    : null;

  // D-06 / #582 (v1) + #678 v2: the title-row VERIFICATION badge — orthogonal to
  // `approvalBadge` above (approval = human sign-off; verification = evidence via
  // traceability AND/OR explicit evidence records). `verificationToneOf` itself
  // returns `null` for a non-verifiable type or while still loading, so this simply
  // passes the state through — no separate "hide it" branch needed, matching the
  // tree/card's "no entry ⇒ render nothing" convention for a state that isn't
  // applicable.
  //
  // #678 v2: prefer `effective_state` (v1's derived state widened by any CURRENT,
  // passing evidence record) over the bare v1 `state`, so recording evidence via the
  // dialog below visibly updates THIS badge even when no trace link exists at all —
  // the acceptance line "the rollup reported to the UI reflects evidence records AND
  // trace links". Falls back to `state` when `effective_state` is absent (an older
  // cached payload, or a test double that only stubs v1's shape) — `?? ` rather than
  // `||` so an explicit `null` (not-applicable) is not overridden by the fallback.
  const verificationBadge = verificationToneOf(
    verificationState?.effective_state ?? verificationState?.state);

  // Header id chip (#174 Part 1b) — honours the showHumanIds preference instead
  // of always preferring display_id; falls back to the raw selectedItemId when
  // the item hasn't loaded yet.
  const showHumanIds = usePref('showHumanIds');
  const idDisplay = item ? resolveIdDisplay(item, showHumanIds) : { primary: selectedItemId, secondary: null };

  // Item-type chit (#231) — prefer the type's configured human label (loaded
  // alongside mainFields above); fall back to a title-cased form of the raw
  // type key (always available from the item DTO) so the chit still shows
  // something even before/without the richer project-scoped type lookup.
  const typeChipLabel = itemTypeLabel || (item?.type
    ? item.type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    : null);

  // Retype <Select> (#197 recovery path) — only offered once the project's type
  // list has loaded (`availableTypes`); the current selection is derived by
  // matching the item's `type_id` against that list (not `item.type`, which for a
  // CUSTOM type is the raw type_id, not a human key — see dto.py's
  // `_type_key_from_id`).
  const currentTypeKey = availableTypes.find((t) => t.id === item?.type_id)?.key || '';

  // Save-status indicator (#145/#328/#160) — mirrors statusBadge's badge/dot
  // convention. Order matters: a plain item READ (isFetchingItem) reads as
  // "Loading…", NOT "Saving…" — the latter is reserved for an actual WRITE in
  // flight (`loading`). Before #328 both shared `loading`, so navigating between
  // requirements flashed "Saving…" over the outgoing item's stale content.
  // #160 folds in the autosave hook's own status (idle/saving/saved/
  // offline-queued/reconnecting/error) below the explicit manual-Save state
  // (`loading`) — an in-flight CLICKED Save always wins the badge over a
  // background autosave status.
  const saveIndicator = isFetchingItem
    ? { tone: 'info', label: 'Loading…' }
    : loading
      ? { tone: 'warning', label: 'Saving…' }
      : autosave.status === 'offline-queued'
        ? { tone: 'error', label: 'Offline — changes queued' }
        : autosave.status === 'reconnecting'
          ? { tone: 'info', label: 'Reconnecting…' }
          : autosave.status === 'saving'
            ? { tone: 'warning', label: 'Saving…' }
            : autosave.status === 'error'
              ? { tone: 'error', label: 'Save failed' }
              : hasChanges
                ? { tone: 'warning', label: 'Unsaved changes' }
                : { tone: 'success', label: 'Saved' };

  // A container is a parent node in the hierarchy. The item-detail payload
  // exposes a derived top-level `type` key (dto.py); legacy data may carry it
  // as an attribute, and the lazy-tree annotations expose child_count — accept
  // any of these so the Document tab shows for every container shape.
  const isContainer = !!item && (
    item.type === 'container' ||
    item.attributes?.type === 'container' ||
    (typeof item.child_count === 'number' && item.child_count > 0)
  );

  // #387: the ordered list of tab keys. Attributes and Comments take the first two
  // slots — Comments FIRST when the item has any comments, otherwise second (right
  // after Attributes). Breakdown (containers only) is always third, then the rest.
  // Building the order as data (rather than hard-coded indices) is what lets the
  // Comments tab move without every other TabPanel's index having to change.
  const hasComments = (commentCount || 0) > 0;
  const tabOrder = [
    ...(hasComments ? ['comments', 'attributes'] : ['attributes', 'comments']),
    ...(isContainer ? ['breakdown'] : []),
    'relationships',
    'impact',
    'history',
    ...(isContainer ? ['document'] : []),
  ];
  // Until the user PINS a tab (via a click), the active tab is ALWAYS the first
  // one in the current order — Comments when the item has comments, else
  // Attributes. This supersedes #342's container→Breakdown default: every item,
  // container or leaf, now opens on tab index 0.
  //
  // #1045: this is the ONLY place the active tab is derived, and it re-runs on
  // every render — so it already re-derives against whatever `tabOrder` the
  // current render has, key-first, with no numeric position involved. If
  // `pickedTabKey` is not (yet) a member of the current `tabOrder` — the item's
  // own fetch hasn't grown it to include Breakdown/Document yet, or the strip
  // hasn't rendered at all — it is kept as-is rather than silently replaced by
  // `tabOrder[0]`: MUI (below) shows no tab as "selected" for that instant,
  // which self-corrects the moment `tabOrder` catches up, instead of the old
  // bug's "the click landed, then the strip moved under it" jump to whatever
  // is now first.
  //
  // ...with ONE exception, found red-teaming the fix above. "Keep the pin until
  // `tabOrder` catches up" is right for a key that is merely LATE, and wrong for one
  // that is GONE. Breakdown and Document are gated on `isContainer`, which reads
  // `child_count > 0` for an item whose own `type` is not literally 'container' — so
  // deleting the last child while pinned to Breakdown drops that key out of
  // `tabOrder` permanently. Nothing would then match, leaving the panel blank with no
  // tab highlighted, and unlike the transient it never self-corrects.
  //
  // `item` is the signal that separates the two: `isContainer` is `!!item && (...)`,
  // so every key that is only LATE is late precisely because `item` is still null.
  // Once `item` is loaded, a picked key missing from `tabOrder` is missing for good.
  // The comment-count race that #1045 is about cannot reach this branch at all —
  // `hasComments` reorders 'comments' and 'attributes', it never removes either.
  const pickedIsGone = !!pickedTabKey && !!item && !tabOrder.includes(pickedTabKey);
  const activeTabKey = (pickedIsGone ? null : pickedTabKey) ?? tabOrder[0];
  const commentsTabLabel = commentCount ? `Comments (${commentCount})` : 'Comments';

  // #799 probe-measured: a stable identity for this prop. Inline, it was a new function
  // every render, and CommentsTab reports its count from useEffect([comments,
  // onCountChange]) -> refreshHistory() -> setHistory(new array) -> re-render -> new
  // identity -> fires again. 10 history GETs for ONE item view + one comment.
  const handleCommentCountChange = useCallback((n) => {
    setCommentCount(n);
    refreshHistory();
  }, [refreshHistory]);

  // #799: refresh History when the tab is OPENED. Coupling the only refresh to the
  // comment COUNT changing is fragile by construction — it makes a timeline's freshness
  // depend on an unrelated component's state transition. Opening a timeline is exactly
  // when it must be current, and the Impact tab already establishes fetch-on-activation
  // in this file (#182). refreshHistory's historyReqIdRef guard makes the extra fetch
  // safe when it overlaps the item-switch load: only the most recently issued response
  // is allowed to land.
  //
  // #821: and re-fetch whenever a mutation has landed since the displayed timeline was
  // requested. `historyAppliedMutationRef` starts at -1 for each item, so a plain
  // activation with no mutations still fetches — #799's behaviour is unchanged. What is new
  // is that a response issued BEFORE a mutation no longer ends the story: it leaves the
  // applied generation behind the current one, and this effect issues a fresh request.
  useEffect(() => {
    if (activeTabKey !== 'history' || !selectedItemId) return;
    if (historyAppliedMutationRef.current >= historyMutationSeq) return;
    refreshHistory();
  }, [activeTabKey, selectedItemId, historyMutationSeq, refreshHistory]);

  // Show loading state — first load (no item yet) fills the card with a spinner.
  // #328: keyed on the READ flag (isFetchingItem) as well, since the initial
  // fetch no longer raises `loading`. `loading && !item` is kept for the rare
  // case an action runs before any item is set.
  if ((isFetchingItem || loading) && !item) {
    return (
      <Card sx={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Card>
    );
  }

  // Show error state
  if (error && !item) {
    return (
      <Card sx={{ height: '100%', p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Card>
    );
  }

  // Show placeholder when no item is selected
  if (!selectedItemId) {
    return (
      <Card sx={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          Select an item from the tree to view details
        </Typography>
      </Card>
    );
  }

  // Retype control (#197) — lives in the main form between Title and
  // Description (not the top chit bar — its rendering there was visually
  // inconsistent with the other form fields, offset top-left). Styled the
  // same way every other field in the form is (a labelled `TextField select`,
  // matching FieldRenderer's `enum` case) so it reads as one of the fields,
  // not a stray control.
  const typeSelectControl = availableTypes.length > 0 && (
    <TextField
      select
      label="Type"
      size="small"
      fullWidth
      data-testid="item-type-select"
      value={currentTypeKey}
      onChange={(e) => handleRetype(e.target.value)}
      slotProps={{ htmlInput: { 'aria-label': 'Item type' } }}
    >
      {availableTypes.map((t) => (
        <MenuItem key={t.id} value={t.key}>{t.label}</MenuItem>
      ))}
    </TextField>
  );

  // #816/#817: the demotable chips as VALUES, so each renders exactly once and MOVES
  // rather than disappearing. #816 asks for auto-hiding to keep the status row one line;
  // #817 asks that "the status items can overflow into the space between them" on the
  // mobile nav row, and the approved mockup shows both — a demoted chip drops into the gap
  // between Prev Item and Next Item instead of vanishing. Rendering them from one map is
  // what keeps that a MOVE: a copy in each slot would duplicate every data-testid and
  // break every strict-mode locator that names one.
  //
  // The measurement does not care where a chip currently lives. `naturalWidths` caches the
  // first non-zero width it sees, and `measure` sums only the ROW's own children for the
  // fixed part, so a chip in the gap neither re-measures as 0 nor counts against the room
  // it is no longer occupying.
  const demotableChips = {
    'item-type': typeChipLabel ? (
      <TypeChipLink key="item-type" typeId={item?.type_id} label={typeChipLabel} chipKey="item-type">
        <span
          className="badge accent"
          data-testid="item-type-chit"
          data-chip-key="item-type"
          ref={chipFit.registerChip('item-type')}
          title={item?.type_id ? undefined : `Item type: ${typeChipLabel}`}
        >
          {typeChipLabel}
        </span>
      </TypeChipLink>
    ) : null,
    'approval-badge': approvalBadge ? (
      <span key="approval-badge" className={`badge ${approvalBadge.className}`}
            data-testid="approval-badge" data-chip-key="approval-badge"
            ref={chipFit.registerChip('approval-badge')}>
        <span className={`dot ${approvalBadge.className}`} />
        {approvalBadge.label}
      </span>
    ) : null,
    'verification-badge': verificationBadge ? (
      <span key="verification-badge" className={`badge ${verificationBadge.className}`}
            data-testid="verification-badge" data-chip-key="verification-badge"
            ref={chipFit.registerChip('verification-badge')}
            title={verificationState?.basis || undefined}>
        <span className={`dot ${verificationBadge.className}`} />
        {verificationBadge.label}
      </span>
    ) : null,
    // #1031: 'record-verification' no longer lives here — see ItemDetailsToolbar below,
    // which renders the same control (same data-testid, same gating on `verificationBadge`)
    // as a normal actions-bar button instead of a squeezed status-row chip.
  };
  // In the row when it fits; in the mobile nav row's gap when it does not.
  const inRow = (key) => (chipFit.hidden.has(key) ? null : demotableChips[key]);
  const overflowed = DEMOTE_ORDER
    .filter((key) => chipFit.hidden.has(key) && demotableChips[key]);

  return (
    <Card sx={{
      height: '100%',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      // #238 follow-up: on mobile the whole card scrolls as ONE unit (title →
      // description → tabs → action bar → done) instead of a fixed-height frame
      // with an inner independently-scrolling region — see the content Box below.
      overflow: isMobile ? 'auto' : 'hidden',
      bgcolor: 'background.paper',
      border: 'none',
      borderRadius: 0,
      boxShadow: 'none',
      // #182 follow-up: the MuiCard theme override (surfaces.js) applies a 16px
      // padding to EVERY card root — a third horizontal-padding layer on top of
      // CardContent + TabPanel that the PO flagged as far too much on a phone.
      // Drop it to 0 on mobile (CardContent/TabPanel still supply a small side
      // gutter); desktop keeps the themed 16px.
      p: { xs: 0, sm: 2 },
      position: 'relative' // Important for absolute positioning of buttons
    }}
    ref={swipeRef}
    data-testid="item-detail-card"
    data-has-unsaved-changes={hasChanges ? 'true' : 'false'}>
      <CardContent sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        py: 2,
        // #182 follow-up: on a phone, 16px of CardContent side-padding (stacked
        // on top of the TabPanel's own 16px) eats a big chunk of a narrow
        // screen — the PO flagged the whole item-detail view as over-padded
        // horizontally. Cut side padding hard on mobile; full padding on desktop.
        // #238 follow-up (desktop): the RIGHT side only keeps a hairline gutter —
        // CardContent is the PARENT of the scrollable content Box below, so any
        // padding-right here insets that whole box (scrollbar included) away
        // from the card's true edge, reading as a scrollbar "floating" mid-card
        // instead of sitting flush against it. The content's own right-side
        // breathing room now comes from padding ON the scrollable box itself
        // (see below) — padding on the scrolling element doesn't push its OWN
        // scrollbar inward the same way, since the scrollbar renders at that
        // element's own border edge, between it and the padding.
        pl: { xs: 0.5, sm: 2 },
        pr: { xs: 0.5, sm: 0.5 },
        // #238 follow-up: 80px was a rough, oversized guess at the sticky
        // action bar's real height (~56px: 8px+8px vertical padding + a
        // ~36-40px button row) — the excess read as "wasted"/unusable height
        // above the bar. Trimmed closer to the bar's actual footprint, still
        // with a small safety margin. Moved to the scrollable box below for
        // the same parent-vs-scrolling-element reason as the right padding.
        pb: isMobile ? 2 : 0,
        '&:last-child': { pb: isMobile ? 2 : 0 }
      }}>
        {/* Show any errors at the top */}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* #145: a leftover local draft from an unclean exit — restore or discard, never silent */}
        {draftPrompt && (
          <Alert severity="info" sx={{ mb: 2 }}
            action={(
              <>
                <Button color="inherit" size="small" onClick={handleRestoreDraft} data-testid="draft-restore">
                  Restore
                </Button>
                <Button color="inherit" size="small" onClick={handleDiscardDraft} data-testid="draft-discard">
                  Discard
                </Button>
              </>
            )}>
            You have unsaved edits from a previous session ({new Date(draftPrompt.savedAt).toLocaleString()}).
          </Alert>
        )}

        {/* #159/#145: someone else saved this item since it was loaded here */}
        {conflict && (
          <Alert severity="warning" sx={{ mb: 2 }}
            action={(
              <>
                <Button color="inherit" size="small" onClick={handleConflictReload} data-testid="conflict-reload">
                  Reload latest
                </Button>
                <Button color="inherit" size="small" onClick={() => setMergeDialogOpen(true)} data-testid="conflict-merge">
                  Merge
                </Button>
                <Button color="inherit" size="small" onClick={handleConflictOverwrite} data-testid="conflict-overwrite">
                  Keep my edits
                </Button>
              </>
            )}>
            {conflict.message || 'This item was changed elsewhere since it was loaded.'}
            {conflict.current?.title ? ` Current title: "${conflict.current.title}".` : ''}
          </Alert>
        )}

        {/* #161: field-level merge (mine vs theirs, with a diff) — the third
            resolution option alongside Reload/Keep-mine above. */}
        <ConflictMergeDialog
          open={mergeDialogOpen && !!conflict}
          onClose={() => setMergeDialogOpen(false)}
          mine={{ title, description }}
          theirs={{ title: conflict?.current?.title, description: conflict?.current?.description }}
          onMerge={handleConflictMerge}
        />

        {/* #193: an AI agent proposed archiving this item (MCP archive_requirement) — it
            stays fully visible/searchable until a human confirms or cancels here. */}
        {item && item.attributes?.status === 'archive_proposed' && (
          <Alert severity="error" sx={{ mb: 2 }} data-testid="archive-proposed-alert"
            action={(
              <>
                <Button color="inherit" size="small" onClick={handleConfirmArchive} data-testid="confirm-archive">
                  Confirm Archive
                </Button>
                <Button color="inherit" size="small" onClick={handleCancelArchive} data-testid="cancel-archive">
                  Cancel Proposal
                </Button>
              </>
            )}>
            An AI agent proposed archiving this item
            {item.attributes?.archive_requested_by ? ` (requested by ${item.attributes.archive_requested_by})` : ''}
            {item.attributes?.archive_reason ? `: "${item.attributes.archive_reason}"` : ''}.
            It remains fully visible until a human confirms or cancels.
          </Alert>
        )}

        {/* #1084: a human closes a change request as rejected, withdrawn or superseded,
            with a reason. An agent can only propose that closure. */}
        <ChangeRequestClosurePanel item={item} onItemUpdated={applyServerItem} onError={setError} />

        {/* Title + ID + status — mockup .titlerow. #238: on mobile the title gets
            its own full-width row (it was being squeezed to near-nothing by the
            id/type/status cluster) and that cluster wraps to a second row below.
            #197 follow-up: the retype Select no longer renders here at all — it
            reads inconsistently with the other form fields in this compact chit
            row, so it now lives in the main form between Title and Description
            (see typeSelectControl's render point below). Only the read-only
            type chit (#231) stays here, next to the id. */}
        <div className="titlerow" style={{ marginBottom: 10 }}>
          <input
            className="t-page title-edit"
            placeholder="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            aria-label="Item title (editable)"
            title="Click to edit title"
            /* #379: don't let the id/type/status meta cluster squeeze the title down
               to a few characters on narrow screens — reserve a sensible minimum
               (capped at 100% so it can't overflow a <300px viewport). `.titlerow`
               wraps (styles/screens.css) so the meta drops onto its own row when
               there isn't room for both. */
            style={{ flex: 1, minWidth: 'min(300px, 100%)', outline: 'none', color: 'inherit', fontFamily: 'inherit' }}
          />
          <div className="titlerow-meta" ref={chipFit.rowRef}>
            <span className="idchip" data-chip-key="idchip" title={idDisplay.secondary || idDisplay.primary}>
              {idDisplay.primary}
            </span>
            {/* Item-type chit (#231) — the type this item is actually stamped
                with, next to its id. Defined above with the other demotable chips so it
                can move into the mobile nav row's gap rather than disappear (#816/#817). */}
            {inRow('item-type')}
            {/* Full id for a11y/testing (the chip shows a truncated UUID). */}
            <span style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0 }}>
              ID: {selectedItemId}
            </span>
            {/* Save-status indicator (#145) — same badge/dot convention as statusBadge
                below. Mobile (#238): dot only, no label text — the colour already
                carries the meaning and the text crowded this row. */}
            {item && !conflict && (
              <>
                <span className={`badge ${saveIndicator.tone}`} data-testid="save-status" data-chip-key="save-status" title={saveIndicator.label}>
                  <span className={`dot ${saveIndicator.tone}`} />
                  <span className="badge-label">{saveIndicator.label}</span>
                </span>
                {/* #170: the visible badge hides its label on mobile and conveys
                    Saving…/Saved/Unsaved by colour alone — invisible to a screen
                    reader. This polite live region announces the same state change. */}
                <span
                  role="status"
                  aria-live="polite"
                  data-testid="save-status-live"
                  style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0 }}
                >
                  {saveIndicator.label}
                </span>
              </>
            )}
            {statusBadge && (
              <span className={`badge ${statusBadge.tone}`}>
                {statusBadge.tone !== 'neutral' && <span className={`dot ${statusBadge.tone}`} />}
                {statusBadge.label}
              </span>
            )}
            {/* #569: the approval-rollup badge — reflects the SAME state the tree
                dot renders for this item (hidden while pending/nothing decided). */}
            {inRow('approval-badge')}
            {/* D-06 / #582 (v1): the DERIVED, type-scoped verification badge —
                evidence-backed (a verifies/tests/validates trace link), NOT the
                retired free-text status attribute. Orthogonal to approvalBadge
                above (approval = human sign-off on wording; verification =
                evidence the requirement is met — both can and do surface
                independently). Hidden entirely for a non-verifiable type
                (container, test_case, ...) and while still loading — same
                "no state ⇒ no badge" rule approvalBadge follows. F-23 / REQ-6109
                (#607, WCAG 1.4.1): same convention as approvalBadge above — the
                state is never colour-only because the LABEL text always says it
                ("Verified"/"Not verified"), not just the dot's colour. */}
            {inRow('verification-badge')}
            {/* #1031: the "Record verification" control used to render right here as a
                squeezed status-row chip — moved to ItemDetailsToolbar (bottom actions bar)
                below, same data-testid/gating, styled like a normal action-bar button. */}
            {/* F-23 / REQ-6109 (#607, WCAG 2.2 SC 1.4.1/4.1.3): approving here updates
                the tree's dot for this item WITHOUT any navigation — the visible badge
                above is silent to a screen reader when it merely reflows in place. Same
                visually-hidden `role="status" aria-live="polite"` pattern as
                save-status-live so the new decision (or a clear back to Pending) is
                actually announced. Rendered whenever the rollup has been fetched
                (including the neutral 'pending' state after an Unapprove), not gated on
                `approvalBadge` (which hides for 'pending' — the live region must NOT). */}
            {approvalState && (
              <span
                role="status"
                aria-live="polite"
                data-testid="approval-status-live"
                style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0 }}
              >
                Approval: {approvalToneOf(approvalState.state, approvalState.stale_state).label}
              </span>
            )}
            {/* F-12 / REQ-6102 (#592, D-05): the decision reads fresh only because a
                restore (or an archive/unarchive cycle) brought the signed-off content
                back. That is correct — what is live IS what was approved — but it must
                not be SILENT, or a rollback quietly resurrects a lapsed sign-off. */}
            {approvalState?.restored && (
              <span className="badge neutral" data-testid="approval-restored-badge"
                    title="Signed off against an earlier revision; that content was later restored, so the decision still applies">
                restored content
              </span>
            )}
            {/* #569/#1031: Approve / Unapprove used to render right here, pinned to
                chip height (18px) to match the row beside it — moved to ItemDetailsToolbar
                (bottom actions bar) below, same data-testid, same `item && !isContainer`
                gate, styled like a normal action-bar button instead of a squeezed pill.
                F-14 / REQ-6104 (#599, D-02): a container's approval is INDEPENDENT of its
                descendants, NOT a rollup — the button stays scoped to leaves purely because
                a container's own approval is set from its row in its PARENT's Breakdown tab
                (ItemBlock renders an ApprovePill for containers too, same as any leaf), not
                because a container "has no state of its own". The container's own approval
                still surfaces on this page via `approvalBadge` above. */}
            {/* #817 desktop: Prev/Next sit at the RIGHT-HAND END of this chip bar,
                pushed there with margin-left:auto. No extra row, and the title keeps
                its width — an earlier draft flanking the title row was rejected.
                Hidden on mobile, where the nav row below carries them instead. */}
            <div className="itemnav itemnav-inline" data-testid="item-nav-desktop">
              <button
                type="button"
                className="itemnav-btn"
                data-testid="item-nav-prev"
                disabled={!prevItemId}
                onClick={() => navigateToNeighbour(prevItemId)}
                title={prevItemId ? 'Previous item (Alt+←)' : 'This is the first visible item'}
                aria-label="Previous item"
              >
                ‹ Prev
              </button>
              <button
                type="button"
                className="itemnav-btn"
                data-testid="item-nav-next"
                disabled={!nextItemId}
                onClick={() => navigateToNeighbour(nextItemId)}
                title={nextItemId ? 'Next item (Alt+→)' : 'This is the last visible item'}
                aria-label="Next item"
              >
                Next ›
              </button>
            </div>
          </div>
        </div>

        {/* #817 mobile: the nav row. Rendered always and hidden on desktop by CSS rather
            than switched on a JS breakpoint, so there is no render-time media query to
            disagree with the stylesheet and no flash of the wrong layout on first paint.
            Prev left, Next right; the status chips above overflow into the gap. */}
        <div className="itemnav itemnav-row" data-testid="item-nav-mobile">
          <button
            type="button"
            className="itemnav-btn"
            data-testid="item-nav-prev-mobile"
            disabled={!prevItemId}
            onClick={() => navigateToNeighbour(prevItemId)}
            aria-label="Previous item"
          >
            ‹ Prev Item
          </button>
          {/* #817: "the status items can overflow into the space between them". A chip
              demoted out of the status row lands HERE rather than disappearing — one
              element, moved, so its data-testid stays unique. Empty when everything fits,
              which is the desktop case and any roomy phone. */}
          <div className="itemnav-overflow" data-testid="item-nav-overflow">
            {overflowed.map((key) => demotableChips[key])}
          </div>
          <button
            type="button"
            className="itemnav-btn"
            data-testid="item-nav-next-mobile"
            disabled={!nextItemId}
            onClick={() => navigateToNeighbour(nextItemId)}
            aria-label="Next item"
          >
            Next Item ›
          </button>
        </div>

        {/* F-08 / REQ-6098 (#603, D-08): the justification the backend requires before it
            will let this approval supersede another reviewer's live rejection. Same
            component the Breakdown pill uses, so the two paths cannot drift apart. */}
        <ApprovalReasonDialog
          open={overrideDialogOpen}
          mode="override"
          itemTitle={item?.title || selectedItemId || ''}
          onCancel={() => setOverrideDialogOpen(false)}
          onConfirm={(text) => {
            setOverrideDialogOpen(false);
            doToggleApproval({ justification: text });
          }}
        />

        {/* #678 v2: explicit verification evidence records — existing records +
            staleness, and a short add-record form (method/result/evidence). */}
        <VerificationRecordDialog
          open={verificationDialogOpen}
          itemId={selectedItemId}
          itemTitle={item?.title || selectedItemId || ''}
          onClose={() => setVerificationDialogOpen(false)}
          onRecorded={handleVerificationRecorded}
        />

        {/* Scrollable content area - make it fill available space. #238 follow-up:
            on mobile this must NOT flex-grow to fill the card — with short content
            (e.g. no description/attributes yet) that stretched a "dead" empty gap
            between the actual content and the action bar below, even after
            un-sticking the bar from position:absolute. Natural height on mobile
            (the Card above scrolls as a whole instead) means the action bar sits
            directly after whatever content there actually is. */}
        {/* #328: while switching to another item the previous item's content is
            still mounted until the new read resolves — dim it and mark it aria-busy
            so it isn't presented as the current, up-to-date item (the "format
            changed but content hadn't updated" confusion in the report). Sub-second,
            so a dim beats a full skeleton swap that would flash the layout. */}
        <Box
          data-testid="item-detail-content"
          aria-busy={isFetchingItem}
          sx={{
          flex: isMobile ? 'none' : 1,
          minHeight: isMobile ? 'auto' : 0,
          overflow: isMobile ? 'visible' : 'auto',
          opacity: isFetchingItem ? 0.5 : 1,
          transition: 'opacity 120ms ease',
          pointerEvents: isFetchingItem ? 'none' : 'auto',
          mb: 2, // Small margin to separate content from buttons
          // #238 follow-up: right/bottom breathing room now lives HERE (on the
          // scrolling element itself) instead of on CardContent (its parent) —
          // see the long comment on CardContent's sx above for why that fixes
          // the "floating scrollbar" look. ~64px bottom clears the sticky
          // action bar's real footprint (a bit tighter than the previous 80px
          // guess) with a small safety margin.
          pr: { xs: 0, sm: 1.5 },
          pb: isMobile ? 0 : '64px'
        }}>
          {/* Form fields — mockup .desc card wrapping Type + Description +
              main-area meta-model fields as ONE bordered group (#197
              follow-up): these previously each had their own `.desc` box, so
              the form rendered as several stacked outlines instead of a
              single one running from Type through the last main-area field,
              stopping before the Attributes/History tab bar. Horizontal
              padding trimmed to ~25% (#197 follow-up) — CardContent above
              already contributes 16px, so the full 12px here read as
              excessive. */}
          <Box className="desc" sx={{ mb: 2, p: '9px 3px', display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Retype control (#197) — between Title and Description, styled
                as a regular form field (TextField select) instead of the
                bare-Select chit-bar control it used to be. Only offered once
                the project's type list has loaded (`availableTypes`). */}
            {typeSelectControl}
            <div>
              {/* #333: markdown-native description editor (MDXEditor). Stored
                  format IS markdown — no conversion at this boundary. The
                  Markdown/Rich-text switch was removed with the markdown-native
                  cutover (TinyMCE is legacy, no user-facing path). */}
              <div className="t-sub" style={{ marginBottom: 5 }}>Description</div>
              <RichTextField
                value={description}
                onChange={handleEditorChange}
                minHeight={(typeof window !== 'undefined' && window.innerWidth <= 640) ? 220 : 210}
                tinymceInit={{
                // Fixed height (no autoresize): autoresize collapsed short/empty
                // descriptions to ~150px and ignored autoresize_min_height, making
                // the description box too small (T1.14). A fixed height + internal
                // scroll is predictable for both short and long requirements.
                // Shorter on phones — a fixed 420px editor was a huge empty box
                // on mobile (most of the screen). #342 quick win: desktop drops
                // 420 -> 210 (the Breakdown tab is now the primary reading
                // surface for a container's content; `resize:'both'` still lets
                // anyone pull it taller for heavy editing). Phone is unchanged.
                height: (typeof window !== 'undefined' && window.innerWidth <= 640) ? 220 : 210,
                min_height: (typeof window !== 'undefined' && window.innerWidth <= 640) ? 200 : 210,
                menubar: false,
                statusbar: true, // Enable status bar for resize handle
                resize: 'both', // Allow vertical and horizontal resizing
                plugins: ['image', 'table', 'link', 'lists', 'code'],
                toolbar: 'bold italic | formatselect | bullist numlist | link image table code',
                content_style: `
                  body {
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
                    font-size: 14px;
                    margin: 8px;
                    padding: 4px;
                  }
                  .tox-tinymce {
                    border: 1px solid #ccc !important;
                  }
                  .tox .tox-toolbar__group {
                    padding: 0 !important;
                  }
                  .tox .tox-toolbar {
                    background: none !important;
                    padding: 0 !important;
                  }
                  .tox .tox-tbtn {
                    height: 24px !important;
                    width: 24px !important;
                    margin: 0 !important;
                    padding: 0 !important;
                  }
                  .tox .tox-toolbar__primary {
                    background: none !important;
                    border-bottom: 1px solid #ccc !important;
                    padding: 0 !important;
                  }
                  .tox .tox-tbtn--select {
                    padding: 0 4px !important;
                    width: auto !important;
                  }
                  .tox .tox-tbtn__select-label {
                    font-size: 12px !important;
                  }                `,
                toolbar_location: 'top',
                toolbar_sticky: true,
                skin: 'oxide',
                content_css: 'default',
                branding: false,
                elementpath: false,
              }}
              />
            </div>

            {/* Main-area meta-model fields (#196/#197) — configured per item
                type via the project's "Item types & fields" editor. Part of
                the SAME bordered group as Type/Description above (#197
                follow-up) — no longer its own separate `.desc` box. */}
            {mainFields.length > 0 && mainFields.map((f) => (
              // Keyed on item+field (#197 follow-up): keying on f.id alone survives
              // switching between items of the same type, so a field with per-item
              // mount-time state (e.g. SvgImageField's "hidden unless empty" default)
              // never resynced when the real value arrived after the initial
              // (value-less) render — it stayed however it was first mounted.
              <FieldRenderer key={`${selectedItemId}-${f.id}`} field={f} value={getFieldValue(f.key)}
                onChange={(v) => setFieldValue(f.key, v)} />
            ))}
          </Box>

          {/* Tabs section. #238: scrollable + auto scroll-buttons so the last tab
              (History[/Document]) is reachable by touch-scroll with a visible
              end-indicator on a narrow phone, instead of silently clipping off
              the edge of the screen — same fix as the admin-area tabs (#239). */}
          <Tabs
            value={activeTabKey}
            onChange={handleTabChange}
            aria-label="Item details tabs"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              minHeight: 36,
              '& .MuiTab-root': { minHeight: 36, py: 0 }
            }}
          >
            {/* #387: tabs are rendered from `tabOrder` (data-driven) so the
                Comments tab can slide to the front once the item has comments,
                without any hard-coded per-tab indices. The Comments label carries
                its live "(n)" count. Keys map 1:1 to the TabPanels below.
                #1045: `value={key}` is explicit (MUI otherwise defaults a Tab's
                value to its POSITION in this array) — that is what lets a click
                identify the tab by its stable key instead of by an index a
                reorder can invalidate; see handleTabChange. */}
            {tabOrder.map((key) => (
              <Tab
                key={key}
                value={key}
                label={key === 'comments'
                  ? commentsTabLabel
                  : key.charAt(0).toUpperCase() + key.slice(1)}
              />
            ))}
          </Tabs>

          {/* Tab Panels. #387/#1045: each panel's `index` is its own stable KEY,
              matched directly against `activeTabKey` — no numeric position, so
              panels stay matched to their tab however the order changes. A key
              absent from `tabOrder` (Breakdown/Document on a leaf) simply never
              equals `activeTabKey`; those panels are also gated by `isContainer`
              so they aren't rendered regardless. */}
          {isContainer && (
            <TabPanel value={activeTabKey} index="breakdown">
              {/* #530 (follow-up to #520): approvals set from here do NOT live-refresh
                  an already-open History tab — Breakdown's approve control only ever
                  targets a descendant of this container (never the container/
                  selectedItemId itself, since there's no root-level approve control),
                  so a "refresh only if the approved id === the open item" condition
                  can never fire. Unlike comments/AI-review (which DO live-refresh,
                  see onCountChange / handleReviewWithAI below), approval-history is
                  refresh-on-next-load only: it shows correctly on tab-switch/reload. */}
              <BreakdownTab
                itemId={selectedItemId}
                getCachedReview={getCachedReview}
                setCachedReview={setCachedReview}
                onNavigateToItem={onNavigateToItem}
                reviewChangeCount={reviewChangeCount}
              />
            </TabPanel>
          )}

          <TabPanel value={activeTabKey} index="attributes">
            <AttributesTab
              attributes={attributes}
              setAttributes={setAttributes}
            />
          </TabPanel>

          <TabPanel value={activeTabKey} index="relationships">
            <RelationshipsTab
              relationships={relationships}
              handleLink={handleLink}
              handleDeleteRelationship={handleDeleteRelationship}
              pendingRelRemovals={pendingRelRemovals}
            />
          </TabPanel>

          {/* LAZY: only fetches the impact tree while this tab is active (#182). */}
          <TabPanel value={activeTabKey} index="impact">
            <ImpactTab itemId={selectedItemId} />
          </TabPanel>

          <TabPanel value={activeTabKey} index="history">
            <HistoryTab
              history={history}
              handleCompareVersion={handleCompareVersion}
              handleRestoreVersion={handleRestoreVersion}
              onOpenComment={() => setPickedTabKey('comments')}
            />
          </TabPanel>

          {/* LAZY: TabPanel mounts its children only while it is the active tab,
              so DocumentTab (and its export fetch) is created only when the user
              actually activates the Document tab — never on item select or while
              another tab is open. DocumentTab caches the result per item id. */}
          {isContainer && (
            <TabPanel value={activeTabKey} index="document">
              <DocumentTab
                itemId={selectedItemId}
                getCached={getCachedDocument}
                setCached={setCachedDocument}
              />
            </TabPanel>
          )}

          {/* Comments tab (#358) — position is data-driven (#387): first when the
              item has comments, else second after Attributes. `onCountChange` keeps
              the parent's count (and thus this tab's order + "(n)" badge) live as
              comments are added/deleted while the tab is open. */}
          <TabPanel value={activeTabKey} index="comments">
            <CommentsTab
              itemId={selectedItemId}
              onCountChange={handleCommentCountChange}
              // #821: fires the instant a write RESOLVES, from CommentsTab's async
              // handler rather than from a render effect. Switching to History unmounts
              // this component (TabPanel renders `value === index && children`), so a
              // signal that needs one more render is lost exactly when the user switches
              // tabs straight after commenting — which is the reported bug.
              onMutated={noteHistoryMutation}
            />
          </TabPanel>
        </Box>

        {/* Toolbar */}
        <ItemDetailsToolbar
          hasChanges={hasChanges}
          loading={loading}
          handleSave={handleSave}
          handleCancel={handleCancel}
          handleDelete={handleDelete}
          handleLink={handleLink}
          handleOpenAddDialog={handleOpenAddDialog}
          // #1031: Approve/Unapprove and Record verification moved down from the status
          // bar into this actions bar — same gating each carried up there (leaf-only for
          // Approve; verifiable-type-only for Record verification, mirrored by
          // `verificationBadge` since that badge is itself null for a non-verifiable type
          // or while still loading).
          showApproval={!!item && !isContainer}
          approvalLabel={approvalState?.mine === 'approved' ? 'Unapprove' : 'Approve'}
          approvalTooltip={approvalState?.mine === 'approved'
            ? 'You approved this item — click to clear your approval.'
            : 'Approve this item as reviewed and correct as written.'}
          handleToggleApproval={handleToggleApproval}
          showVerification={!!verificationBadge}
          handleOpenVerification={() => setVerificationDialogOpen(true)}
          aiFeatures={{
            // #520: an AI review interleaves into the SAME merged History feed
            // #389 built for comments — refresh it live so a review run while
            // the History tab is open shows up without a tab-switch round trip
            // (mirrors CommentsTab's onCountChange wiring below).
            handleReviewWithAI: async (...args) => {
              await aiFeatures.handleReviewWithAI(...args);
              refreshHistory();
            },
            handleCheckConsistency: aiFeatures.handleCheckConsistency,
            handleDecompose: aiFeatures.handleDecompose,
            handleGenerateTests: aiFeatures.handleGenerateTests,
            handleIdentifyEntities: aiFeatures.handleIdentifyEntities,
            onCalculateLimitsClick: aiFeatures.handleCalculateLimits,
            onImplementClick: aiFeatures.handleImplement
          }}
        />

        {/* Dialogs */}
        {/* Version Comparison Dialog */}
        <Dialog
          open={compareDialogOpen}
          onClose={() => setCompareDialogOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            Compare Version {selectedVersion} → Current
          </DialogTitle>
          <DialogContent>
            {compareLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : compareSnapshot ? (
              <Box>
                {/* Title diff (only show if it changed) */}
                {htmlToText(compareSnapshot.title) !== htmlToText(title) && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Title</Typography>
                    <DiffViewer
                      original={htmlToText(compareSnapshot.title)}
                      modified={htmlToText(title)}
                    />
                  </Box>
                )}

                {/* Description diff */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Description</Typography>
                  <DiffViewer
                    original={htmlToText(compareSnapshot.description)}
                    modified={htmlToText(description)}
                  />
                </Box>

                {/* Attribute changes (added / changed / removed) */}
                {(() => {
                  const attrChanges = computeAttributeDiff(compareSnapshot.attributes, attributes);
                  return (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>Attributes</Typography>
                      {attrChanges.length === 0 ? (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>No attribute changes.</Typography>
                      ) : (
                        attrChanges.map((c) => (
                          <Box key={c.name} sx={{ mb: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                              {c.name} · {c.kind}
                            </Typography>
                            <DiffViewer original={c.before} modified={c.after} />
                          </Box>
                        ))
                      )}
                    </Box>
                  );
                })()}

                {/* Relationship/link changes vs. the item's current relationships. */}
                {(() => {
                  const { added, removed, label } = computeRelationshipDiff(
                    compareSnapshot.relationships, relationships);
                  if (added.length === 0 && removed.length === 0) return null;
                  return (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>Relationships</Typography>
                      {added.map((rel) => (
                        <div key={`add-${label(rel)}`}>
                          <span className="diff-add">+ {rel.link_type} → {label(rel)}</span>
                        </div>
                      ))}
                      {removed.map((rel) => (
                        <div key={`del-${label(rel)}`}>
                          <span className="diff-del">− {rel.link_type} → {label(rel)}</span>
                        </div>
                      ))}
                    </Box>
                  );
                })()}

                {/* Side-by-side raw snapshot for context */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 2 }}>
                  <Box>
                    <Typography variant="subtitle2">
                      Version {selectedVersion}
                      {compareSnapshot.author ? ` · ${compareSnapshot.author}` : ''}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>
                      {htmlToText(compareSnapshot.description)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2">Current</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>
                      {htmlToText(description)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ) : (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                No version data available.
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            {compareSnapshot && (
              <Button
                color="primary"
                onClick={() => {
                  setCompareDialogOpen(false);
                  handleRestoreVersion(selectedVersion);
                }}
              >
                Restore this version
              </Button>
            )}
            <Button onClick={() => setCompareDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* AI Dialog */}
        <AiDialog
          open={aiFeatures.aiDialogOpen}
          onClose={aiFeatures.handleCloseAiDialog}
          title={aiFeatures.aiDialogTitle}
          content={aiFeatures.aiDialogContent}
          dialogType={aiFeatures.aiDialogType}
          refreshTree={refreshTree}
        />

        {/* Entities Dialog */}
        <EntitiesDialog
          open={aiFeatures.entitiesDialogOpen}
          onClose={aiFeatures.handleCloseEntitiesDialog}
          data={aiFeatures.entitiesData}
        />

        {/* AddDialog Component */}
        <AddDialog
          open={addDialogOpen}
          onClose={handleCloseAddDialog}
          onAdd={handleAddItem}
          parentId={selectedItemId}
          isContainer={isAddingContainer}
          projectId={item?.project_id}
        />

        {/* DeleteDialog Component */}
        <DeleteDialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          itemTitle={title}
          itemType={item?.attributes?.type === 'container' ? 'container' : 'requirement'}
          childCount={deleteChildCount}
        />

        {/* In-app confirmation modal (replaces native alerts): cascade archive
            (#347) and version restore. */}
        <ConfirmDialog
          open={!!confirm}
          onClose={() => setConfirm(null)}
          onConfirm={() => { const c = confirm; setConfirm(null); if (c) c.onConfirm(); }}
          title={confirm?.title || ''}
          message={confirm?.message || ''}
          severity={confirm?.severity || null}
          confirmColor={confirm?.confirmColor || 'primary'}
          confirmLabel={confirm?.confirmLabel || 'Confirm'}
          testId={confirm?.testId || 'itemdetails-confirm-dialog'}
        />
        
        {/* Calculations Dialog */}
        <CalculationsDialog
          open={calculations.calculationsDialogOpen}
          onClose={calculations.handleCloseCalculationsDialog}
          data={calculations.calculationsData}
          loading={calculations.calculationsLoading}
          error={calculations.calculationsError}
        />
        
        {/* ReviewFeedbackDialog for AI review workflow */}
        <ReviewFeedbackDialog
          open={aiFeatures.reviewFeedbackDialogOpen}
          onClose={aiFeatures.handleCloseReviewFeedbackDialog}
          data={aiFeatures.reviewFeedbackData}
          originalContent={aiFeatures.originalContent}
          onApplyRecommendations={aiFeatures.handleApplyRecommendations}
        />
        
        {/* DecomposeDialog for AI decomposition workflow */}
        <DecomposeDialog
          open={aiFeatures.decomposeDialogOpen}
          onClose={aiFeatures.handleCloseDecomposeDialog}
          data={aiFeatures.decomposeData}
          onCreateRequirements={aiFeatures.handleCreateDecomposedRequirements}
        />
        
        {/* TestCaseDialog for AI test case generation workflow */}
        <TestCaseDialog
          open={aiFeatures.testCaseDialogOpen}
          onClose={aiFeatures.handleCloseTestCaseDialog}
          data={aiFeatures.testCaseData}
          onCreateTestCases={aiFeatures.handleCreateTestCases}
        />

        {/* ImplementationDialog for AI "implement requirement" workflow.
            Previously never rendered in this (active) view, so clicking
            Implement ran the API call but no dialog appeared (T1.8). */}
        <ImplementationDialog
          open={aiFeatures.implementationDialogOpen}
          onClose={aiFeatures.handleCloseImplementationDialog}
          data={aiFeatures.implementationData}
          originalContent={aiFeatures.originalContent}
          onApplyImprovedRequirement={aiFeatures.handleApplyImprovedRequirement}
          currentModel={aiFeatures.currentModel}
          availableModels={aiFeatures.availableModels}
          onModelChange={aiFeatures.handleModelChange}
          onRefineImplementation={aiFeatures.handleRefineImplementation}
        />

        {/* LinkDialog Component - Using UnifiedContainerSelector */}
        <UnifiedContainerSelector
          open={linkDialogOpen}
          onClose={() => setLinkDialogOpen(false)}
          onSelect={(id, name) => {
            // Show the link type selection dialog
            setSelectedLinkItem({id, name});
            setLinkTypeDialogOpen(true);
          }}
          title="Select Item to Link"
          selectMode="all"
          fallbackToLegacy={true}
          /* #375: only same-project items are valid relationship targets. */
          projectId={item?.project_id}
        />
        
        {/* Link Type Dialog - For selecting relationship type */}
        <Dialog
          open={linkTypeDialogOpen}
          onClose={() => setLinkTypeDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Select Relationship Type</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Typography variant="body2" gutterBottom>
              Select relationship type for linking to: {selectedLinkItem?.name}
            </Typography>
            <Box sx={{ mt: 2 }}>
              <FormControl fullWidth>
                <InputLabel id="link-type-label">Relationship Type</InputLabel>
                <Select
                  labelId="link-type-label"
                  value={linkType}
                  onChange={(e) => setLinkType(e.target.value)}
                  label="Relationship Type"
                >
                  {/* #374: labels read from THIS item's side — the link is stored
                      outgoing (this item → target), so `parent` means the TARGET is
                      this item's parent. "Parent of"/"Child of" read backwards, so
                      they're "Parent"/"Child" to match how the Relationships tab
                      and impact views now label the very same link. */}
                  <MenuItem value="related">Related to</MenuItem>
                  <MenuItem value="parent">Parent (the target is this item&apos;s parent)</MenuItem>
                  <MenuItem value="child">Child (the target is this item&apos;s child)</MenuItem>
                  <MenuItem value="depends_on">Depends on</MenuItem>
                  <MenuItem value="derived_from">Derived from</MenuItem>
                  <MenuItem value="verifies">Verifies</MenuItem>
                  <MenuItem value="decomposed_into">Decomposed into</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLinkTypeDialogOpen(false)}>Cancel</Button>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => {
                // Process the link with the selected type
                if (selectedLinkItem) {
                  handleLinkSubmit(selectedLinkItem.id, selectedLinkItem.name);
                  setLinkTypeDialogOpen(false);
                }
              }}
            >
              Create Link
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
}

ItemDetails.propTypes = {
  selectedItemId: PropTypes.string,
  refreshTree: PropTypes.func,
  // #567: optimistic single-node update/remove (useTreeState.js) — Save/Delete
  // use these instead of `refreshTree` so an edit to THIS item doesn't reload
  // the whole tree. Optional: falls back to `refreshTree` (or a no-op) when a
  // caller doesn't pass them, so older/test call sites keep working.
  patchNode: PropTypes.func,
  removeNode: PropTypes.func,
  // #569: notifies the caller's approval-state map after a successful
  // Approve/Unapprove — no tree/network refetch either way.
  onApprovalChange: PropTypes.func,
  // #342: lets the Breakdown tab's id-chip deep-link change the tree
  // selection (e.g. `(id) => setSelected([id])` in RequirementsTreeView).
  // Optional — omitted in tests/other callers, id chips just don't navigate.
  onNavigateToItem: PropTypes.func,
};
